// File: src/pages/AdminEvents.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLoginWrapper from '../components/AdminLoginWrapper'; // <--- NEW import
import AdminPromoters from '../components/AdminPromoters';
import AdminColdOutreach from '../components/admin/AdminColdOutreach';
import AdminUsers from '../components/admin/AdminUsers';
import TRAIT_AXES from '../utils/traitAxes';  
import AdminEventTemplates from '../components/admin/AdminEventTemplates';
import RecruitingPortal from '../components/admin/RecruitingPortal';
import ReferralPartnersManager from '../components/admin/ReferralPartnersManager';
import EventExperienceFeedbackTable from '../components/admin/EventExperienceFeedbackTable';
import ReferralPartnerApplicants from '../components/admin/ReferralPartnerApplicants';
import AdminEventLocation from '../components/admin/AdminEventLocation';
import AdminMarketsModal from '../components/admin/AdminMarketsModal';
import MemberApplications from '../components/admin/MemberApplications';

function getPromoterEmailById(list, id) {
  if (!id) return '';
  return (list || []).find(p => String(p._id) === String(id))?.email || '';
}

function getPromoterById(list, id) {
  if (!id) return null;
  return (list || []).find(p => String(p._id) === String(id)) || null;
}

function tenureLabelFrom(dateLike) {
  if (!dateLike) return '';
  const start = new Date(dateLike);
  if (Number.isNaN(start.getTime())) return '';
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months <= 0) return 'New host';
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} hosting`;
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? 'year' : 'years'} hosting`;
}


// Map an eventId to a human name from the events list
function getEventNameById(events, id) {
  if (!id) return '(No eventId)';
  const found = (events || []).find(e => String(e._id) === String(id));
  return found?.name || `(Event ${String(id).slice(-6)})`;
}


function ageLabel(ev) {
  if (!ev?.ageGateOn) return '-';

  const min = Math.max(18, Number(ev.ageMin || 18));
  const max = ev.ageMax == null || ev.ageMax === '' ? null : Number(ev.ageMax);

  if (max == null) return `${min}+`;
  if (max === min) return `${min} only`;
  return `${min}–${max}`;
}

function formatTime12h(time) {
  if (!time) return '';

  const t = String(time).trim();

  if (t.includes('AM') || t.includes('PM')) return t;

  const [hRaw, mRaw] = t.split(':');
  const h = Number(hRaw);
  const m = Number(mRaw ?? 0);

  if (!Number.isFinite(h)) return t;

  const hour12 = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? 'PM' : 'AM';

  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function timeLabel(ev) {
  const s = formatTime12h(ev?.startTime12 || '');
  const e = formatTime12h(ev?.endTime12 || '');

  return s || e ? `${s || ''} ~ ${e || ''}` : '-';
}

function sizeLabel(ev) {
  if (ev?.capacity != null && ev.capacity !== '') {
    return ev.capacity === 0 ? 'No max' : String(ev.capacity);
  }

  return '-';
}

function genderGateLabel(ev) {
  const mode = String(ev?.mode || '').toLowerCase();

  const raw =
    mode === 'neutral'
      ? ev?.neutralGenderGate
      : mode === 'matchmaking'
      ? ev?.classicGenderGate
      : ev?.genderGate;

  const g = String(raw || '').toLowerCase();

  if (!g || g === 'all') return 'All';
  if (g === 'men_only' || g === 'men-only') return 'Men only';
  if (g === 'women_only' || g === 'women-only') return 'Women only';

  return raw || '-';
}

function orientationLabel(ev) {
  const mode = String(ev?.mode || '').toLowerCase();

  const raw =
    mode === 'neutral'
      ? ev?.neutralOrientation
      : mode === 'matchmaking'
      ? ev?.classicOrientation
      : ev?.orientationType;

  const o = String(raw || '').toLowerCase();

  if (!o || o === 'open') return 'Open';
  if (o === 'straight' || o === 'heterosexual') return 'Heterosexual';
  if (o === 'gay_men') return 'Gay Men';
  if (o === 'gay_women') return 'Gay Women';
  if (o === 'lgbtq') return 'LGBTQ+';

  return raw || '-';
}

function hostLabel(ev, promoters) {
  const promoter =
    ev?.promoterId && typeof ev.promoterId === 'object'
      ? ev.promoterId
      : (promoters || []).find(
          p => String(p._id) === String(ev?.promoterId)
        );

  const img = promoter?.imageUrl || '';
  const name = promoter?.name || '—';
  const email = promoter?.email || '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          overflow: 'hidden',
          background: '#f3f4f6',
        }}
      >
        {img && (
          <img
            src={img}
            alt={name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </div>

      <div>
        <div style={{ fontWeight: 900, fontSize: 13 }}>{name}</div>
        {email && <div style={{ fontSize: 12, color: '#6b7280' }}>{email}</div>}
      </div>
    </div>
  );
}
// Hard-coded presets for demonstration.
const ICEBREAKER_PRESETS = {
  dating: [
    "What's your ideal first date?",
    "What's your favorite way to spend a weekend?",
    "What's the best piece of dating advice you've received?",
  ],
  friendship: [
    "What's a fun hobby or interest you're exploring?",
    "If you could travel anywhere right now, where?",
    "What's your go-to comfort movie or TV show?",
  ],
  networking: [
    "What do you do professionally?",
    "What's a recent career goal you've accomplished?",
    "What's the best advice you've received about work-life balance?",
  ],
};


// Category/tag options used by discovery bubbles.
// Keep ids in lowercase, kebab-case to match CategoryPills.
const TAG_OPTIONS = [
  { id: 'friendship', label: '🫶 Friendship' },
  { id: 'wellness', label: '🧘 Wellness' },
  { id: 'dating', label: '💘 Dating' },
  { id: 'nightlife', label: '🎉 Nightlife' },
  { id: 'art', label: '🎨 Art' },
  { id: 'women-only', label: '👯‍♀️ Women Only' },
  { id: 'men-only', label: '👬 Men Only' },
  { id: 'museum', label: '🏛️ Museum' },
  { id: 'music', label: '🎵 Music' },
  { id: 'sports', label: '🏀 Sports' },
  { id: 'fitness', label: '🏃‍♀️ Fitness' },
  { id: 'networking', label: '🤝 Networking' },
  { id: 'food', label: '🍔 Food' },
  { id: 'wine', label: '🍷 Wine' },
  { id: 'outdoors', label: '🌲 Outdoors' },
  { id: 'movies', label: '🎬 Movies' },
  { id: 'lgbtq', label: '🌈 LGBTQ' },
  { id: 'college', label: '🎓 College' },
  { id: 'learning', label: '📚 Learning' },
];



export default function AdminEvents() {

  const [clothing,   setClothing]   = useState('');   // ⭐ NEW
const [venueType, setVenueType]  = useState('');   // ⭐ NEW
const [vibeTag, setVibeTag] = useState('');

// --- Age Gate ---
const [ageGateOn, setAgeGateOn] = useState(false);
const [ageMin,    setAgeMin]    = useState('18');   // keep as string for inputs
const [ageMax,    setAgeMax]    = useState('');     // '' => null (no upper bound)


  const [capacity, setCapacity] = useState(0);
  const [price, setPrice] = useState('');  // 👈 Add this line

  const [soldOutOverride, setSoldOutOverride] = useState(false);
const [displayAttendeeCountOverride, setDisplayAttendeeCountOverride] = useState('');
const [displayCapacityOverride, setDisplayCapacityOverride] = useState('');

 // ------------------------------------------------------------
// ADMIN VIEW STATE
// ------------------------------------------------------------

const [adminTab, setAdminTab] = useState('events');

const [eventsView, setEventsView] = useState('main');
// 'main' | 'past_live'

const [events, setEvents] = useState([]);

const [templateRefreshKey, setTemplateRefreshKey] = useState(0);
const [savingTemplateId, setSavingTemplateId] = useState('');
const [creatingTemplate, setCreatingTemplate] = useState(false);

const [issuedCodes, setIssuedCodes] = useState({});
// { [eventId]: '123456' }


// ------------------------------------------------------------
// FEEDBACK
// ------------------------------------------------------------

const [feedbackList, setFeedbackList] = useState([]);

const eventFeedback = React.useMemo(
  () =>
    feedbackList.filter(
      f => f?.type === 'event_experience'
    ),
  [feedbackList]
);

const problems = React.useMemo(
  () =>
    feedbackList.filter(f =>
      ['problem', 'issue'].includes(
        String(f?.type || '').toLowerCase()
      )
    ),
  [feedbackList]
);

const suggestions = React.useMemo(
  () =>
    feedbackList.filter(
      f =>
        String(f?.type || '').toLowerCase() ===
        'suggestion'
    ),
  [feedbackList]
);


// Group event feedback by eventId
const eventFeedbackByEvent = React.useMemo(() => {
  const map = new Map();

  for (const fb of eventFeedback) {
    const key = fb?.eventId
      ? String(fb.eventId)
      : 'NO_EVENT_ID';

    if (!map.has(key)) {
      map.set(key, []);
    }

    map.get(key).push(fb);
  }

  return map;
}, [eventFeedback]);


// ------------------------------------------------------------
// REVEAL RESET HELPERS
// ------------------------------------------------------------

function revealFieldsChanged(prev, next) {
  const pd = String(prev?.eventDate || '');
  const ps = String(prev?.startTime12 || '');
  const pm = Number(
    prev?.revealAtMinutes ?? -1
  );

  const nd = String(next?.eventDate || '');
  const ns = String(next?.startTime12 || '');
  const nm = Number(
    next?.revealAtMinutes ?? -1
  );

  return (
    pd !== nd ||
    ps !== ns ||
    pm !== nm
  );
}


async function callRevealReset(
  eventId,
  { cleanup = true } = {}
) {
  const adminToken =
    localStorage.getItem('adminToken') || '';

  const qs = cleanup
    ? '?cleanup=1'
    : '';

  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/reveal-mode/${eventId}/reset${qs}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      }
    );
  } catch (err) {
    console.error(
      '[reveal reset] →',
      err?.response?.data ||
        err.message
    );
  }
}


// ------------------------------------------------------------
// ADD / EDIT FORM STATE
// ------------------------------------------------------------

const [showAddForm, setShowAddForm] =
  useState(false);

const [showEditForm, setShowEditForm] =
  useState(false);

  const [showMarketsModal, setShowMarketsModal] = useState(false);

const [editEvent, setEditEvent] =
  useState(null);


// ------------------------------------------------------------
// COMMON EVENT FIELDS
// ------------------------------------------------------------

const [name, setName] = useState('');
const [address, setAddress] = useState('');
const [latitude, setLatitude] = useState('');
const [longitude, setLongitude] = useState('');
const [resolvedLocation, setResolvedLocation] = useState(null);
const [locationBusy, setLocationBusy] = useState(false);

function handleLocationResolved(location) {
  setResolvedLocation(location);
  setLatitude(location ? String(location.latitude) : '');
  setLongitude(location ? String(location.longitude) : '');
}

function getMarketLocationPayload() {
  if (locationBusy) {
    throw new Error('Wait for the location lookup to finish.');
  }

  if (
    !resolvedLocation ||
    resolvedLocation.address !== address.trim() ||
    !Number.isFinite(resolvedLocation.latitude) ||
    !Number.isFinite(resolvedLocation.longitude) ||
    !resolvedLocation.timeZone
  ) {
    throw new Error('Lookup the event address before saving.');
  }

  if (!/^[a-fA-F0-9]{24}$/.test(resolvedLocation.marketId || '')) {
    throw new Error('Choose or create a market before saving.');
  }

  return {
    address: resolvedLocation.address,
    latitude: resolvedLocation.latitude,
    longitude: resolvedLocation.longitude,
    timeZone: resolvedLocation.timeZone,
    marketId: resolvedLocation.marketId,
  };
}

const [radius, setRadius] =
  useState('5280');

const [description, setDescription] =
  useState('');

const [
  occupancyCount,
  setOccupancyCount,
] = useState('');

const [
  whereToMeetSpot,
  setWhereToMeetSpot,
] = useState('');

const [
  whereToMeetNote,
  setWhereToMeetNote,
] = useState('');

const [
  whereToMeetImageUrl,
  setWhereToMeetImageUrl,
] = useState('');

const [
  whereToMeetUploading,
  setWhereToMeetUploading,
] = useState(false);


// ------------------------------------------------------------
// DATE / TIME / VENUE
// ------------------------------------------------------------

const [eventDate, setEventDate] =
  useState('');

const [startTime12, setStartTime12] =
  useState('');

const [endTime12, setEndTime12] =
  useState('');

const [hostedAt, setHostedAt] =
  useState('');


// ------------------------------------------------------------
// EVENT SETTINGS
// ------------------------------------------------------------

const [
  useAutoOccupancy,
  setUseAutoOccupancy,
] = useState(true);

const [
  maxGroupSize,
  setMaxGroupSize,
] = useState(2);

const [purposeTag, setPurposeTag] =
  useState('');

const [tags, setTags] =
  useState([]);

const [
  orientationType,
  setOrientationType,
] = useState('');

const [genderGate, setGenderGate] =
  useState('all');

const [affinity, setAffinity] =
  useState(false);


// ------------------------------------------------------------
// CLASSIC AUDIENCE
// ------------------------------------------------------------

const [
  classicGenderGate,
  setClassicGenderGate,
] = useState('all');

const [
  classicOrientation,
  setClassicOrientation,
] = useState('open');


// ------------------------------------------------------------
// LANE CAPS
// ------------------------------------------------------------

const [maleCap, setMaleCap] =
  useState('');

const [femaleCap, setFemaleCap] =
  useState('');


// ------------------------------------------------------------
// MIXER SETTINGS
// ------------------------------------------------------------

const [
  roundTraits,
  setRoundTraits,
] = useState([]);

const [
  roundStartTimes,
  setRoundStartTimes,
] = useState([]);

const [
  roundPrompts,
  setRoundPrompts,
] = useState([]);

const [eventType, setEventType] =
  useState('matchmaking');

const [quizLength, setQuizLength] =
  useState(3);

const [reveal, setReveal] = useState({
  orientation: 'open',
  revealAtMinutes: 20,
});

const [
  revealTimeISO,
  setRevealTimeISO,
] = useState('');


// ------------------------------------------------------------
// NEUTRAL AUDIENCE
// ------------------------------------------------------------

const [
  neutralGenderGate,
  setNeutralGenderGate,
] = useState('all');

const [
  neutralOrientation,
  setNeutralOrientation,
] = useState('open');

const [
  neutralMaleCap,
  setNeutralMaleCap,
] = useState('');

const [
  neutralFemaleCap,
  setNeutralFemaleCap,
] = useState('');

const [
  neutralLaneCapsOn,
  setNeutralLaneCapsOn,
] = useState(false);


// ------------------------------------------------------------
// NEUTRAL LANE CAP HELPERS
// ------------------------------------------------------------

const neutralCapNum =
  capacity === '' ||
  capacity == null
    ? 0
    : Number(capacity) || 0;

const neutralOrientationKey =
  String(
    neutralOrientation || 'open'
  ).toLowerCase();

const neutralGenderGateKey =
  String(
    neutralGenderGate || 'all'
  ).toLowerCase();


function normalizeNeutralOrientation(v) {
  const s = String(
    v || 'open'
  ).toLowerCase();

  if (
    s === 'lgbtq+' ||
    s === 'lgbtq'
  ) {
    return 'lgbtq';
  }

  return s;
}


function coerceOrientationForGenderGate(
  gate,
  orientation
) {
  const g = String(
    gate || 'all'
  ).toLowerCase();

  const o =
    normalizeNeutralOrientation(
      orientation
    );

  if (
    g === 'all' &&
    (
      o === 'gay_men' ||
      o === 'gay_women'
    )
  ) {
    return 'open';
  }

  if (
    g === 'men_only' &&
    o === 'gay_women'
  ) {
    return 'open';
  }

  if (
    g === 'women_only' &&
    o === 'gay_men'
  ) {
    return 'open';
  }

  return o;
}


function coerceGenderGateForOrientation(
  gate,
  orientation
) {
  const g = String(
    gate || 'all'
  ).toLowerCase();

  const o =
    normalizeNeutralOrientation(
      orientation
    );

  if (o === 'gay_men') {
    return 'men_only';
  }

  if (o === 'gay_women') {
    return 'women_only';
  }

  return g;
}


function applyNeutralGateAndOrientation(
  nextGate,
  nextOrientation
) {
  const gate1 = String(
    nextGate ||
      neutralGenderGate ||
      'all'
  ).toLowerCase();

  const ori1 =
    normalizeNeutralOrientation(
      nextOrientation ??
        neutralOrientation ??
        'open'
    );

  const gate2 =
    coerceGenderGateForOrientation(
      gate1,
      ori1
    );

  const ori2 =
    coerceOrientationForGenderGate(
      gate2,
      ori1
    );

  if (
    gate2 !==
    neutralGenderGateKey
  ) {
    setNeutralGenderGate(gate2);
  }

  if (
    ori2 !==
    neutralOrientationKey
  ) {
    setNeutralOrientation(ori2);
  }
}


useEffect(() => {
  if (eventType !== 'neutral') {
    return;
  }

  applyNeutralGateAndOrientation(
    neutralGenderGateKey,
    neutralOrientationKey
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  eventType,
  neutralGenderGateKey,
  neutralOrientationKey,
]);


const neutralIsGaySingleGender =
  neutralOrientationKey ===
    'gay_men' ||
  neutralOrientationKey ===
    'gay_women';


const neutralIsSingleGenderGate =
  neutralGenderGateKey ===
    'men_only' ||
  neutralGenderGateKey ===
    'women_only';


const neutralLaneCapsHardBlocked =
  eventType === 'neutral' &&
  (
    neutralCapNum <= 0 ||
    neutralIsGaySingleGender ||
    neutralIsSingleGenderGate
  );


const neutralLaneCapsEnabled =
  eventType === 'neutral' &&
  neutralLaneCapsOn &&
  !neutralLaneCapsHardBlocked;


const neutralLaneCapsDisabled =
  eventType === 'neutral' &&
  !neutralLaneCapsEnabled;


const neutralMaleCapNum =
  Number(neutralMaleCap || 0);

const neutralFemaleCapNum =
  Number(neutralFemaleCap || 0);


const neutralLaneCapsValid =
  !neutralLaneCapsEnabled ||
  (
    neutralMaleCapNum +
      neutralFemaleCapNum ===
    neutralCapNum
  );


useEffect(() => {
  if (!neutralLaneCapsHardBlocked) {
    return;
  }

  if (neutralLaneCapsOn) {
    setNeutralLaneCapsOn(false);
  }

  if (
    neutralMaleCap !== '' ||
    neutralFemaleCap !== ''
  ) {
    setNeutralMaleCap('');
    setNeutralFemaleCap('');
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [neutralLaneCapsHardBlocked]);


useEffect(() => {
  if (!neutralLaneCapsEnabled) {
    return;
  }

  const bothBlank =
    String(
      neutralMaleCap || ''
    ) === '' &&
    String(
      neutralFemaleCap || ''
    ) === '';

  if (!bothBlank) {
    return;
  }

  if (neutralCapNum <= 0) {
    return;
  }

  const men =
    Math.floor(
      neutralCapNum / 2
    );

  const women =
    neutralCapNum - men;

  setNeutralMaleCap(
    String(men)
  );

  setNeutralFemaleCap(
    String(women)
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  neutralLaneCapsEnabled,
  neutralCapNum,
]);


// ------------------------------------------------------------
// MEDIA
// ------------------------------------------------------------

const [
  galleryList,
  setGalleryList,
] = useState([]);

const [
  galleryUploading,
  setGalleryUploading,
] = useState(false);


const [
  agendaPdfUrl,
  setAgendaPdfUrl,
] = useState('');

const [
  agendaPdfPublicId,
  setAgendaPdfPublicId,
] = useState('');

const [
  agendaPdfName,
  setAgendaPdfName,
] = useState('');

const [
  agendaPdfUploading,
  setAgendaPdfUploading,
] = useState(false);


const [navList, setNavList] =
  useState([]);

const [navUrl, setNavUrl] =
  useState('');

const [navDesc, setNavDesc] =
  useState('');


// ------------------------------------------------------------
// ICEBREAKERS
// ------------------------------------------------------------

const [
  icebreakers,
  setIcebreakers,
] = useState([]);

const [
  newIcebreaker,
  setNewIcebreaker,
] = useState('');

const [
  selectedPreset,
  setSelectedPreset,
] = useState('');


// ------------------------------------------------------------
// HOSTS
// ------------------------------------------------------------

const [promoters, setPromoters] =
  useState([]);

const [
  selectedPromoterId,
  setSelectedPromoterId,
] = useState('');

const [
  promoterMenuOpen,
  setPromoterMenuOpen,
] = useState(false);

const selectedPromoterEmail =
  getPromoterEmailById(
    promoters,
    selectedPromoterId
  );

const selectedPromoter =
  getPromoterById(
    promoters,
    selectedPromoterId
  );


// ------------------------------------------------------------
// EVENT TIMING CONSTANTS
// ------------------------------------------------------------

const EVENT_START_BUFFER_MINUTES =
  10;

const EVENT_END_BUFFER_MINUTES =
  10;

const ROUND_MIN_GAP_MINUTES =
  8;


// ------------------------------------------------------------
// INITIAL LOAD
// ------------------------------------------------------------

useEffect(() => {
  fetchEvents();
  fetchFeedback();
  fetchPromoters();

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


useEffect(() => {
  if (
    adminTab === 'feedback'
  ) {
    fetchFeedback();
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [adminTab]);


// ------------------------------------------------------------
// HOSTS
// ------------------------------------------------------------

const fetchPromoters = async () => {
  const token =
    localStorage.getItem(
      'adminToken'
    );

  if (!token) {
    console.warn(
      'No admin token found, skipping promoters fetch.'
    );
    return;
  }

  try {
    const res =
      await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/promoters`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

    setPromoters(res.data);
  } catch (err) {
    console.error(
      'Failed to fetch promoters:',
      err
    );
  }
};


// ------------------------------------------------------------
// EVENT DATE HELPER
// ------------------------------------------------------------

function isPastEvent(evt) {
  if (!evt.eventDate) {
    return false;
  }

  const today = new Date();

  const [
    year,
    month,
    day,
  ] = evt.eventDate
    .split('-')
    .map(Number);

  const end = new Date(
    year,
    month - 1,
    day,
    23,
    59,
    59,
    999
  );

  if (evt.endTime12) {
    const t =
      evt.endTime12.trim();

    const m = t.match(
      /^(\d{1,2})(?::(\d{2}))?\s*([AP]M)$/i
    );

    if (m) {
      let hh =
        Number(m[1]);

      const mm =
        Number(
          m[2] ?? 0
        );

      const ampm =
        m[3].toLowerCase();

      if (
        ampm === 'pm' &&
        hh !== 12
      ) {
        hh += 12;
      }

      if (
        ampm === 'am' &&
        hh === 12
      ) {
        hh = 0;
      }

      end.setHours(
        hh,
        mm,
        0,
        0
      );
    }
  }

  return today > end;
}


// ------------------------------------------------------------
// FETCH EVENTS
// ------------------------------------------------------------

const fetchEvents = async () => {
  try {
    const token =
      localStorage.getItem(
        'adminToken'
      );

    if (!token) {
      window.alert(
        'Please log in as an admin first.'
      );
      return;
    }

    const eventsRes =
      await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/events/all`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

    /*
     * Your /admin/events/all endpoint returns normal Event
     * documents inside "matchmaking", even when their actual
     * mode is neutral/reveal.
     *
     * So preserve the stored mode instead of forcing everything
     * to matchmaking.
     */
    const standardEvents =
      (
        eventsRes.data
          ?.matchmaking || []
      ).map(e => ({
        ...e,
        mode:
          e.mode ||
          'matchmaking',
      }));


    const mixers =
      (
        eventsRes.data
          ?.mixer || []
      ).map(m => ({
        ...m,

        _id: m._id,

        mode: 'mixer',

        hostedAt:
          m.venue ||
          m.hostedAt ||
          '',

        eventDate:
          m.eventDate,

        startTime12:
          m.startTime12,

        endTime12:
          m.endTime12 || '',

        purposeTag:
          m.purposeTag || '',

        tags:
          Array.isArray(
            m.tags
          )
            ? m.tags
            : [],

        orientationType:
          m.orientationType ||
          '',

        genderGate:
          m.genderGate ||
          'all',

        affinity:
          !!m.affinity,

        audienceLabel:
          m.audienceLabel ||
          '',

        softGateOn:
          !!m.softGateOn,

        maleCap:
          Number(
            m.maleCap || 0
          ),

        femaleCap:
          Number(
            m.femaleCap || 0
          ),

        price:
          m.price || 0,

        useAutoOccupancy:
          m.useAutoOccupancy !==
          undefined
            ? m.useAutoOccupancy
            : true,

        ageGateOn:
          !!m.ageGateOn,

        ageMin:
          m.ageMin ?? 18,

        ageMax:
          m.ageMax ?? null,
      }));


    setEvents([
      ...standardEvents,
      ...mixers,
    ]);

  } catch (err) {
    console.error(
      '[fetchEvents] →',
      err
    );

    alert(
      'Failed to load events'
    );
  }
};
// ------------------------------------------------------------
// FEEDBACK
// ------------------------------------------------------------

const fetchFeedback = async () => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/feedback`
    );

    setFeedbackList(res.data);
  } catch (err) {
    console.error(err);
    alert('Failed to load feedback');
  }
};


// ------------------------------------------------------------
// DATE / TIME HELPERS
// ------------------------------------------------------------

function localDate(dateStr, time24h) {
  const [y, m, d] = dateStr
    .split('-')
    .map(Number);

  const [H, M, S = 0] = time24h
    .split(':')
    .map(Number);

  return new Date(
    y,
    m - 1,
    d,
    H,
    M,
    S
  );
}


function validateRoundTimes(
  eventDate,
  startTime12,
  endTime12,
  roundStartTimes
) {
  const start24 =
    convertTo24Hour(startTime12);

  const end24 =
    convertTo24Hour(endTime12);

  if (
    !eventDate ||
    !start24 ||
    !end24
  ) {
    alert(
      'Set the event date, start time, and end time first.'
    );
    return false;
  }

  const eventStart =
    localDate(
      eventDate,
      start24
    );

  const eventEnd =
    localDate(
      eventDate,
      end24
    );

  const earliestAllowed =
    new Date(
      eventStart.getTime() +
        EVENT_START_BUFFER_MINUTES *
          60000
    );

  const latestAllowed =
    new Date(
      eventEnd.getTime() -
        EVENT_END_BUFFER_MINUTES *
          60000
    );

  const times =
    roundStartTimes.map(
      t =>
        t
          ? new Date(t)
          : null
    );

  if (
    !times.filter(Boolean).length
  ) {
    alert(
      'Please schedule at least one round.'
    );
    return false;
  }

  if (
    times[0] &&
    (
      times[0] <
        earliestAllowed ||
      times[0] >
        latestAllowed
    )
  ) {
    alert(
      `Round 1 must be between ${earliestAllowed.toLocaleTimeString()} and ${latestAllowed.toLocaleTimeString()}.`
    );
    return false;
  }

  for (
    let i = 1;
    i < times.length;
    i++
  ) {
    if (!times[i]) {
      alert(
        `Please pick a start time for Round ${i + 1}.`
      );
      return false;
    }

    if (
      times[i] <=
      times[i - 1]
    ) {
      alert(
        `Round ${i + 1} must start after Round ${i}.`
      );
      return false;
    }

    if (
      (
        times[i] -
        times[i - 1]
      ) /
        60000 <
      ROUND_MIN_GAP_MINUTES
    ) {
      alert(
        `Round ${i + 1} must start at least ${ROUND_MIN_GAP_MINUTES} minutes after Round ${i}.`
      );
      return false;
    }

    if (
      times[i] >
      latestAllowed
    ) {
      alert(
        `Round ${i + 1} can’t start after ${latestAllowed.toLocaleTimeString()}.`
      );
      return false;
    }
  }

  return true;
}


function convertTo24Hour(
  time12h
) {
  if (
    !time12h ||
    typeof time12h !==
      'string'
  ) {
    return null;
  }

  const parts =
    time12h
      .trim()
      .split(' ');

  if (parts.length < 2) {
    return null;
  }

  const [
    time,
    modifier,
  ] = parts;

  if (
    !time ||
    !modifier
  ) {
    return null;
  }

  let [
    hours,
    minutes,
  ] = time
    .split(':')
    .map(Number);

  const mod =
    modifier.toUpperCase?.() ||
    '';

  if (
    mod === 'PM' &&
    hours !== 12
  ) {
    hours += 12;
  }

  if (
    mod === 'AM' &&
    hours === 12
  ) {
    hours = 0;
  }

  return `${String(hours).padStart(
    2,
    '0'
  )}:${String(
    minutes || 0
  ).padStart(
    2,
    '0'
  )}:00`;
}


function hhmmFrom24h(v24) {
  return v24
    ? v24.slice(0, 5)
    : '';
}


function hhmmFromISO(iso) {
  if (!iso) return '';

  const d = new Date(iso);

  return `${String(
    d.getHours()
  ).padStart(
    2,
    '0'
  )}:${String(
    d.getMinutes()
  ).padStart(
    2,
    '0'
  )}`;
}


function isoFromDateAndHHMM(
  dateStr,
  hhmm
) {
  if (
    !dateStr ||
    !hhmm
  ) {
    return '';
  }

  return `${dateStr}T${hhmm}:00`;
}


function generateTimeOptions(
  eventDate,
  eventStart12,
  eventEnd12,
  selectedTimes,
  idx
) {
  if (
    !eventDate ||
    !eventStart12 ||
    !eventEnd12
  ) {
    return [];
  }

  const s24 =
    convertTo24Hour(
      eventStart12
    );

  const e24 =
    convertTo24Hour(
      eventEnd12
    );

  if (
    !s24 ||
    !e24
  ) {
    return [];
  }

  const options = [];

  const eventStart =
    localDate(
      eventDate,
      s24
    );

  const eventEnd =
    localDate(
      eventDate,
      e24
    );

  const earliestAllowed =
    new Date(
      eventStart.getTime() +
        EVENT_START_BUFFER_MINUTES *
          60000
    );

  const latestAllowed =
    new Date(
      eventEnd.getTime() -
        EVENT_END_BUFFER_MINUTES *
          60000
    );

  const prevTime =
    idx > 0 &&
    selectedTimes[
      idx - 1
    ]
      ? new Date(
          selectedTimes[
            idx - 1
          ]
        )
      : null;

  const nextTime =
    idx <
      selectedTimes.length -
        1 &&
    selectedTimes[
      idx + 1
    ]
      ? new Date(
          selectedTimes[
            idx + 1
          ]
        )
      : null;

  for (
    let hour = 0;
    hour < 24;
    hour++
  ) {
    for (
      let minutes = 0;
      minutes < 60;
      minutes++
    ) {
      const hh =
        hour
          .toString()
          .padStart(
            2,
            '0'
          );

      const mm =
        minutes
          .toString()
          .padStart(
            2,
            '0'
          );

      const time =
        localDate(
          eventDate,
          `${hh}:${mm}:00`
        );

      const label =
        `${((hour + 11) % 12) + 1}:${mm} ${
          hour < 12
            ? 'AM'
            : 'PM'
        }`;

      const disabled =
        time <
          earliestAllowed ||
        time >
          latestAllowed ||
        (
          prevTime &&
          time <= prevTime
        ) ||
        (
          nextTime &&
          time >= nextTime
        ) ||
        selectedTimes.some(
          (t, i) =>
            i !== idx &&
            t &&
            Math.abs(
              new Date(t) -
                time
            ) /
              60000 <
              ROUND_MIN_GAP_MINUTES
        );

      options.push({
        label,
        value: label,
        disabled,
      });
    }
  }

  return options;
}


function handleDateChange(
  newDate
) {
  setEventDate(newDate);

  if (
    eventType ===
    'mixer'
  ) {
    setRoundStartTimes(
      Array(
        roundTraits.length
      ).fill('')
    );

    setRoundPrompts(
      Array(
        roundTraits.length
      ).fill('')
    );
  }

  if (
    eventType ===
    'reveal'
  ) {
    setRevealTimeISO('');
  }
}


// ------------------------------------------------------------
// ADD EVENT
// ------------------------------------------------------------

const handleAddEvent =
  async (e) => {
    e.preventDefault();

    // Neutral lane caps
    if (
      eventType ===
      'neutral'
    ) {
      if (
        neutralLaneCapsEnabled &&
        !neutralLaneCapsValid
      ) {
        alert(
          `Neutral lane caps must equal capacity (${neutralCapNum}).`
        );
        return;
      }
    }

    // Mixer rounds
    if (
      eventType ===
      'mixer'
    ) {
      const valid =
        validateRoundTimes(
          eventDate,
          startTime12,
          endTime12,
          roundStartTimes
        );

      if (!valid) {
        return;
      }
    }

    // Age gate
    if (ageGateOn) {
      const min =
        Math.max(
          18,
          Number(
            ageMin || 18
          )
        );

      if (
        !Number.isFinite(
          min
        ) ||
        min < 18
      ) {
        alert(
          'Age min must be ≥ 18'
        );
        return;
      }

      if (
        ageMax !== ''
      ) {
        const max =
          Number(ageMax);

        if (
          !Number.isFinite(
            max
          ) ||
          max < min
        ) {
          alert(
            'Age max must be ≥ min'
          );
          return;
        }
      }
    }

    // Classic hetero lane caps
    if (
      eventType ===
        'matchmaking' &&
      purposeTag ===
        'Dating' &&
      classicOrientation ===
        'heterosexual'
    ) {
      const cap =
        Number(
          capacity
        ) || 0;

      const m =
        Number(
          maleCap
        ) || 0;

      const f =
        Number(
          femaleCap
        ) || 0;

      if (
        cap > 0 &&
        m + f !== cap
      ) {
        alert(
          'For a heterosexual classic event with a max capacity, Men + Women spots must exactly equal total capacity.'
        );
        return;
      }
    }

    // Reveal validation
    if (
      eventType ===
      'reveal'
    ) {
      if (
        !eventDate ||
        !startTime12 ||
        !endTime12
      ) {
        alert(
          'Set Event Date, Start, and End times first.'
        );
        return;
      }

      if (
        !revealTimeISO
      ) {
        alert(
          'Pick a Reveal time.'
        );
        return;
      }

      const hasAtLeastOne =
        Array.isArray(
          icebreakers
        ) &&
        icebreakers.some(
          q =>
            String(
              q || ''
            )
              .trim()
              .length > 0
        );

      if (
        !hasAtLeastOne
      ) {
        alert(
          'Please add at least one icebreaker question for Reveal events.'
        );
        return;
      }
    }

    // Mixer hetero lane caps
    if (
      eventType ===
        'mixer' &&
      purposeTag ===
        'Dating' &&
      orientationType ===
        'heterosexual'
    ) {
      const cap =
        Number(
          capacity
        ) || 0;

      const m =
        Number(
          maleCap
        ) || 0;

      const f =
        Number(
          femaleCap
        ) || 0;

      if (
        cap > 0 &&
        m + f !== cap
      ) {
        alert(
          'For a heterosexual mixer with a max capacity, Men + Women spots must exactly equal total capacity.'
        );
        return;
      }

      const times =
        (
          roundStartTimes ||
          []
        ).filter(
          Boolean
        );

      const promptsOk =
        Array.isArray(
          roundPrompts
        ) &&
        times.length > 0 &&
        times.length ===
          roundPrompts.length &&
        roundPrompts.every(
          p =>
            String(
              p || ''
            )
              .trim()
              .length > 0
        );

      if (
        !promptsOk
      ) {
        alert(
          'Please add a custom icebreaker question for each round (one prompt per scheduled round).'
        );
        return;
      }
    }

    try {
      const token =
        localStorage.getItem(
          'adminToken'
        ) || '';

      // --------------------------------------------------------
      // COMMON PAYLOAD
      // --------------------------------------------------------

      const base = {
        name,
        address,
        latitude,
        longitude,
        radius,
        description,

        agendaPdfUrl,
        agendaPdfPublicId,
        agendaPdfName,

        eventDate:
          eventDate.trim() ||
          '',

        startTime12:
          startTime12.trim() ||
          '',

        endTime12:
          endTime12.trim() ||
          '',

        hostedAt:
          hostedAt.trim() ||
          '',

        capacity:
          capacity === ''
            ? 0
            : Number(
                capacity
              ),

        whereToMeetSpot:
          whereToMeetSpot.trim(),

        whereToMeetNote:
          whereToMeetNote.trim(),

        whereToMeetImageUrl:
          whereToMeetImageUrl.trim(),

        mode:
          eventType,

        promoterId:
          selectedPromoterId ||
          null,

        ageGateOn:
          !!ageGateOn,

        ageMin:
          Math.max(
            18,
            Number(
              ageMin || 18
            )
          ),

        ageMax:
          ageMax === ''
            ? null
            : Number(
                ageMax
              ),
      };


      // --------------------------------------------------------
      // MODE-SPECIFIC PAYLOAD
      // --------------------------------------------------------

      const payload =
        eventType ===
        'mixer'
          ? {
              name,

              eventDate:
                eventDate.trim(),

              startTime12:
                startTime12.trim(),

              endTime12:
                endTime12.trim(),

              venue:
                hostedAt.trim(),

              hostedAt:
                hostedAt.trim(),

              address:
                address.trim(),

              latitude:
                latitude === ''
                  ? 0
                  : Number(
                      latitude
                    ),

              longitude:
                longitude === ''
                  ? 0
                  : Number(
                      longitude
                    ),

              radius:
                radius === ''
                  ? 0
                  : Number(
                      radius
                    ),

              capacity:
                capacity === ''
                  ? 0
                  : Number(
                      capacity
                    ),

              traitAxes:
                (
                  purposeTag ===
                    'Dating' &&
                  orientationType ===
                    'heterosexual'
                )
                  ? roundStartTimes
                      .filter(
                        Boolean
                      )
                      .map(
                        (
                          _,
                          i
                        ) =>
                          `Round ${
                            i +
                            1
                          }`
                      )
                  : roundTraits,

              quizLen:
                Number(
                  quizLength
                ),

              promoterId:
                selectedPromoterId ||
                null,

              vibeTag:
                vibeTag ||
                null,

              mode:
                'mixer',

              description:
                description.trim() ||
                '',

              purposeTag:
                purposeTag.trim() ||
                '',

              orientationType:
                purposeTag ===
                'Dating'
                  ? orientationType ||
                    ''
                  : '',

              genderGate:
                purposeTag ===
                  'Friendship' ||
                purposeTag ===
                  'Networking'
                  ? genderGate ||
                    'all'
                  : 'all',

              affinity:
                !!affinity,

              maleCap:
                purposeTag ===
                  'Dating' &&
                orientationType ===
                  'heterosexual' &&
                Number(
                  capacity
                ) > 0
                  ? Number(
                      maleCap ||
                        0
                    )
                  : 0,

              femaleCap:
                purposeTag ===
                  'Dating' &&
                orientationType ===
                  'heterosexual' &&
                Number(
                  capacity
                ) > 0
                  ? Number(
                      femaleCap ||
                        0
                    )
                  : 0,

              useAutoOccupancy,

              galleryImages:
                galleryList,

              savedUserIds: [],

              occupancyCount:
                Number(
                  occupancyCount
                ) || 0,

              attendees: [],

              roundStartTimes:
                roundStartTimes
                  .filter(
                    Boolean
                  )
                  .map(
                    t =>
                      new Date(
                        t
                      ).toISOString()
                  ),

              roundPrompts,

              price:
                Number(
                  price
                ) || 0,

              clothing:
                clothing.trim(),

              venueType:
                venueType.trim(),

              ageGateOn:
                !!ageGateOn,

              ageMin:
                Math.max(
                  18,
                  Number(
                    ageMin ||
                      18
                  )
                ),

              ageMax:
                ageMax === ''
                  ? null
                  : Number(
                      ageMax
                    ),

              tags:
                Array.from(
                  new Set(
                    (
                      tags ||
                      []
                    ).map(
                      t =>
                        String(
                          t
                        )
                          .toLowerCase()
                          .trim()
                    )
                  )
                ),
            }

          : eventType ===
            'reveal'
          ? {
              ...base,

              occupancyCount:
                Number(
                  occupancyCount
                ) || 0,

              galleryImages:
                galleryList,

              navigationImages:
                navList,

              useAutoOccupancy,

              maxGroupSize:
                Number(
                  maxGroupSize
                ) || 2,

              purposeTag:
                purposeTag.trim() ||
                '',

              icebreakers,

              price:
                Number(
                  price
                ) || 0,

              clothing:
                clothing.trim(),

              venueType:
                venueType.trim(),

              tags:
                Array.from(
                  new Set(
                    (
                      tags ||
                      []
                    ).map(
                      t =>
                        String(
                          t
                        )
                          .toLowerCase()
                          .trim()
                    )
                  )
                ),

              affinity:
                !!affinity,

              revealMode: {
                orientation:
                  reveal.orientation,

                revealAtMinutes:
                  Number(
                    reveal.revealAtMinutes ||
                      0
                  ),
              },
            }

          : eventType ===
            'neutral'
          ? {
              ...base,

              mode:
                'neutral',

              galleryImages:
                galleryList,

              navigationImages:
                navList,

              useAutoOccupancy,

              maxGroupSize:
                Number(
                  maxGroupSize
                ) || 2,

              purposeTag:
                purposeTag.trim() ||
                '',

              icebreakers,

              price:
                Number(
                  price
                ) || 0,

              clothing:
                clothing.trim(),

              venueType:
                venueType.trim(),

              tags:
                Array.from(
                  new Set(
                    (
                      tags ||
                      []
                    ).map(
                      t =>
                        String(
                          t
                        )
                          .toLowerCase()
                          .trim()
                    )
                  )
                ),

              neutralGenderGate,

              neutralOrientation,

              neutralMaleCap:
                Number(
                  neutralMaleCap ||
                    0
                ),

              neutralFemaleCap:
                Number(
                  neutralFemaleCap ||
                    0
                ),

              affinity:
                !!affinity,
            }

          : {
              // Classic matchmaking
              ...base,

              occupancyCount:
                Number(
                  occupancyCount
                ) || 0,

              galleryImages:
                galleryList,

              navigationImages:
                navList,

              navigationSpotDescription:
                '',

              useAutoOccupancy,

              maxGroupSize:
                Number(
                  maxGroupSize
                ) || 2,

              purposeTag:
                purposeTag.trim() ||
                '',

              icebreakers,

              price:
                Number(
                  price
                ) || 0,

              clothing:
                clothing.trim(),

              venueType:
                venueType.trim(),

              tags:
                Array.from(
                  new Set(
                    (
                      tags ||
                      []
                    ).map(
                      t =>
                        String(
                          t
                        )
                          .toLowerCase()
                          .trim()
                    )
                  )
                ),

              classicGenderGate:
                purposeTag ===
                  'Friendship' ||
                purposeTag ===
                  'Networking'
                  ? classicGenderGate
                  : 'all',

              classicOrientation:
                purposeTag ===
                'Dating'
                  ? classicOrientation ===
                    'bisexual'
                    ? 'open'
                    : classicOrientation
                  : 'open',

              affinity:
                !!affinity,

              maleCap:
                purposeTag ===
                  'Dating' &&
                classicOrientation ===
                  'heterosexual' &&
                Number(
                  capacity
                ) > 0
                  ? Number(
                      maleCap ||
                        0
                    )
                  : 0,

              femaleCap:
                purposeTag ===
                  'Dating' &&
                classicOrientation ===
                  'heterosexual' &&
                Number(
                  capacity
                ) > 0
                  ? Number(
                      femaleCap ||
                        0
                    )
                  : 0,
            };


            if (eventType !== 'mixer' && !creatingTemplate) {
  Object.assign(payload, getMarketLocationPayload());
}
      if (
        payload.mode ===
        'neutral'
      ) {
        payload.radius =
          5280;
      }


      if (creatingTemplate) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/admin/event-templates`,
          {
            name: payload.name,
            mode: payload.mode || eventType,
            eventData: payload,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        alert('Template added');
        setTemplateRefreshKey(key => key + 1);
        setCreatingTemplate(false);
      } else {
        const url =
          eventType === 'mixer'
            ? `${process.env.REACT_APP_API_URL}/mixer/events`
            : `${process.env.REACT_APP_API_URL}/admin/events`;

        await axios.post(
          url,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        alert('Event added');
      }

      setShowAddForm(
        false
      );

      clearForm();

      if (!creatingTemplate) {
        fetchEvents();
      }

    } catch (err) {
      alert(
        err.response?.data
          ?.message ||
          err.message
      );
    }
  };


// ------------------------------------------------------------
// EDIT: LOAD EXISTING EVENT
// ------------------------------------------------------------

const handleEditClick =
  (evtObj) => {
    setEditEvent(
      evtObj
    );

    const savedMarketId = String(
  evtObj.marketId?._id ?? evtObj.marketId ?? ''
);

const hasSavedLocation =
  evtObj.latitude != null &&
  evtObj.longitude != null &&
  Number.isFinite(Number(evtObj.latitude)) &&
  Number.isFinite(Number(evtObj.longitude)) &&
  Boolean(evtObj.timeZone) &&
  /^[a-fA-F0-9]{24}$/.test(savedMarketId);

setResolvedLocation(
  hasSavedLocation
    ? {
        address: String(evtObj.address || '').trim(),
        latitude: Number(evtObj.latitude),
        longitude: Number(evtObj.longitude),
        timeZone: evtObj.timeZone,
        marketId: savedMarketId,
        market: evtObj.marketId?._id
          ? evtObj.marketId
          : null,
        place: evtObj.detectedPlace || null,
        mappedMarketId: null,
        mappingKnown: false,
      }
    : null
);

setLocationBusy(false);

    // --------------------------------------------------------
    // CAPACITY
    // --------------------------------------------------------

    const cap =
      evtObj.capacity != null
        ? evtObj.capacity
        : '';

    setCapacity(
      cap === 0
        ? 0
        : String(
            cap
          )
    );


    // --------------------------------------------------------
    // MIXER ROUNDS
    // --------------------------------------------------------

    setRoundTraits(
      evtObj.traitAxes ||
        []
    );

    setRoundStartTimes(
      evtObj
        .roundStartTimes
        ?.length
        ? [
            ...evtObj.roundStartTimes,
          ]
        : Array(
            evtObj
              .traitAxes
              ?.length ||
              0
          ).fill('')
    );

    setRoundPrompts(
      Array.isArray(
        evtObj.roundPrompts
      )
        ? [
            ...evtObj.roundPrompts,
          ]
        : Array(
            evtObj
              .traitAxes
              ?.length ||
              0
          ).fill('')
    );


    // --------------------------------------------------------
    // URGENCY
    // --------------------------------------------------------

    setSoldOutOverride(
      !!evtObj.soldOutOverride
    );

    setDisplayAttendeeCountOverride(
      evtObj
        .displayAttendeeCountOverride ==
      null
        ? ''
        : String(
            evtObj
              .displayAttendeeCountOverride
          )
    );

    setDisplayCapacityOverride(
      evtObj
        .displayCapacityOverride ==
      null
        ? ''
        : String(
            evtObj
              .displayCapacityOverride
          )
    );


    // --------------------------------------------------------
    // CORE FIELDS
    // --------------------------------------------------------

    setName(
      evtObj.name || ''
    );

    setAddress(
      evtObj.address || ''
    );

    setLatitude(
      evtObj.latitude != null
        ? String(
            evtObj.latitude
          )
        : ''
    );

    setLongitude(
      evtObj.longitude != null
        ? String(
            evtObj.longitude
          )
        : ''
    );

    setRadius(
      evtObj.radius != null
        ? String(
            evtObj.radius
          )
        : '5280'
    );

    setDescription(
      evtObj.description ||
        ''
    );

    setOccupancyCount(
      evtObj.occupancyCount !=
      null
        ? String(
            evtObj
              .occupancyCount
          )
        : ''
    );


    // --------------------------------------------------------
    // WHERE TO MEET
    // --------------------------------------------------------

    setWhereToMeetSpot(
      evtObj
        .whereToMeetSpot ||
        ''
    );

    setWhereToMeetNote(
      evtObj
        .whereToMeetNote ||
        ''
    );

    setWhereToMeetImageUrl(
      evtObj
        .whereToMeetImageUrl ||
        ''
    );


    // --------------------------------------------------------
    // EVENT META
    // --------------------------------------------------------

    setPrice(
      evtObj.price != null
        ? String(
            evtObj.price
          )
        : ''
    );

    setClothing(
      evtObj.clothing ||
        ''
    );

    setVenueType(
      evtObj.venueType ||
        ''
    );

    setVibeTag(
      evtObj.vibeTag ||
        ''
    );

    setTags(
      Array.isArray(
        evtObj.tags
      )
        ? evtObj.tags
        : []
    );


    setUseAutoOccupancy(
      evtObj
        .useAutoOccupancy !==
        false
    );

    setMaxGroupSize(
      evtObj.maxGroupSize !=
      null
        ? String(
            evtObj
              .maxGroupSize
          )
        : '2'
    );

    setPurposeTag(
      evtObj.purposeTag ||
        ''
    );


    // --------------------------------------------------------
    // AGE
    // --------------------------------------------------------

    setAgeGateOn(
      !!evtObj.ageGateOn
    );

    setAgeMin(
      String(
        evtObj.ageMin ??
          18
      )
    );

    setAgeMax(
      evtObj.ageMax ==
      null
        ? ''
        : String(
            evtObj.ageMax
          )
    );


    // --------------------------------------------------------
    // MIXER AUDIENCE
    // --------------------------------------------------------

    setOrientationType(
      evtObj.orientationType ||
        ''
    );

    setGenderGate(
      evtObj.genderGate ||
        'all'
    );

    setAffinity(
      !!evtObj.affinity
    );


    // --------------------------------------------------------
    // CLASSIC AUDIENCE
    // --------------------------------------------------------

    setClassicGenderGate(
      evtObj
        .classicGenderGate ||
        'all'
    );

    setClassicOrientation(
      String(
        evtObj
          .classicOrientation ||
          'open'
      ).replace(
        'bisexual',
        'open'
      )
    );


    setMaleCap(
      evtObj.maleCap !=
      null
        ? String(
            evtObj.maleCap
          )
        : ''
    );

    setFemaleCap(
      evtObj.femaleCap !=
      null
        ? String(
            evtObj.femaleCap
          )
        : ''
    );

    if (
      !evtObj.capacity
    ) {
      setMaleCap('');
      setFemaleCap('');
    }


    // --------------------------------------------------------
    // NEUTRAL AUDIENCE
    // --------------------------------------------------------

    setNeutralGenderGate(
      evtObj
        .neutralGenderGate ||
        'all'
    );

    setNeutralOrientation(
      evtObj
        .neutralOrientation ||
        'open'
    );

    setNeutralMaleCap(
      evtObj
        .neutralMaleCap !=
      null
        ? String(
            evtObj
              .neutralMaleCap
          )
        : ''
    );

    setNeutralFemaleCap(
      evtObj
        .neutralFemaleCap !=
      null
        ? String(
            evtObj
              .neutralFemaleCap
          )
        : ''
    );

    const hasNeutralLaneCaps =
      Number(
        evtObj
          .neutralMaleCap ||
          0
      ) > 0 ||
      Number(
        evtObj
          .neutralFemaleCap ||
          0
      ) > 0;

    setNeutralLaneCapsOn(
      hasNeutralLaneCaps
    );


    // --------------------------------------------------------
    // DATE / TIME / VENUE
    // --------------------------------------------------------

    setEventDate(
      evtObj.eventDate ||
        ''
    );

    setStartTime12(
      evtObj.startTime12 ||
        ''
    );

    setEndTime12(
      evtObj.endTime12 ||
        ''
    );

    setHostedAt(
      evtObj.hostedAt ||
        evtObj.venue ||
        ''
    );


    // --------------------------------------------------------
    // MEDIA
    // --------------------------------------------------------

    setGalleryList(
      Array.isArray(
        evtObj.galleryImages
      )
        ? evtObj
            .galleryImages
        : []
    );

    setAgendaPdfUrl(
      evtObj.agendaPdfUrl ||
        ''
    );

    setAgendaPdfPublicId(
      evtObj
        .agendaPdfPublicId ||
        ''
    );

    setAgendaPdfName(
      evtObj.agendaPdfName ||
        ''
    );

    setAgendaPdfUploading(
      false
    );

    setNavList(
      Array.isArray(
        evtObj.navigationImages
      )
        ? evtObj
            .navigationImages
        : []
    );


    // --------------------------------------------------------
    // EVENT MODE
    // --------------------------------------------------------

    setEventType(
      evtObj.mode ||
        'matchmaking'
    );


    // --------------------------------------------------------
    // REVEAL SETTINGS
    // --------------------------------------------------------

    if (
      evtObj.mode ===
        'reveal' &&
      evtObj.revealMode
    ) {
      const raw =
        String(
          evtObj
            .revealMode
            .orientation ||
            'open'
        ).toLowerCase();

      const normalized =
        raw ===
        'bisexual'
          ? 'open'
          : raw;

      setReveal({
        orientation:
          normalized,

        revealAtMinutes:
          Number(
            evtObj
              .revealMode
              .revealAtMinutes ||
              20
          ),
      });

      const s24 =
        convertTo24Hour(
          evtObj
            .startTime12 ||
            ''
        );

      if (
        evtObj.eventDate &&
        s24
      ) {
        const startMs =
          localDate(
            evtObj
              .eventDate,
            s24
          ).getTime();

        const tMs =
          startMs +
          Number(
            evtObj
              .revealMode
              .revealAtMinutes ||
              0
          ) *
            60000;

        const d =
          new Date(
            tMs
          );

        const iso =
          `${evtObj.eventDate}T${String(
            d.getHours()
          ).padStart(
            2,
            '0'
          )}:${String(
            d.getMinutes()
          ).padStart(
            2,
            '0'
          )}:00`;

        setRevealTimeISO(
          iso
        );
      } else {
        setRevealTimeISO(
          ''
        );
      }
    } else {
      setReveal({
        orientation:
          'open',

        revealAtMinutes:
          20,
      });

      setRevealTimeISO(
        ''
      );
    }


    // --------------------------------------------------------
    // MIXER CONFIG
    // --------------------------------------------------------

    if (
      evtObj.traitAxes
    ) {
      setRoundTraits(
        evtObj.traitAxes
      );

      setQuizLength(
        evtObj.quizLen ||
          3
      );
    } else if (
      evtObj.mixerConfig
    ) {
      setQuizLength(
        evtObj
          .mixerConfig
          .quizLength ||
          3
      );

      setRoundTraits(
        evtObj
          .mixerConfig
          .roundTraits ||
          []
      );
    }


    if (
      evtObj
        .roundStartTimes
        ?.length
    ) {
      setRoundStartTimes([
        ...evtObj.roundStartTimes,
      ]);
    } else {
      setRoundStartTimes(
        Array(
          evtObj
            .traitAxes
            ?.length ||
            0
        ).fill('')
      );
    }


    // --------------------------------------------------------
    // ICEBREAKERS
    // --------------------------------------------------------

    setIcebreakers(
      Array.isArray(
        evtObj.icebreakers
      )
        ? evtObj
            .icebreakers
        : []
    );

    setSelectedPreset(
      ''
    );


    // --------------------------------------------------------
    // HOST
    // --------------------------------------------------------

    setSelectedPromoterId(
      evtObj.promoterId
        ? typeof evtObj
            .promoterId ===
          'object'
          ? String(
              evtObj
                .promoterId
                ._id
            )
          : String(
              evtObj
                .promoterId
            )
        : ''
    );

    setPromoterMenuOpen(
      false
    );


    // --------------------------------------------------------
    // OPEN EDIT FORM
    // --------------------------------------------------------

    setShowAddForm(
      false
    );

    setShowEditForm(
      true
    );
  };

  // ------------------------------------------------------------
// EDIT: submit changes
// ------------------------------------------------------------
const handleEditEvent = async (e) => {
  e.preventDefault();

  if (eventType === 'neutral' && neutralLaneCapsEnabled && !neutralLaneCapsValid) {
    alert(`Neutral lane caps must equal capacity (${neutralCapNum}).`);
    return;
  }

  if (eventType === 'mixer') {
    const valid = validateRoundTimes(eventDate, startTime12, endTime12, roundStartTimes);
    if (!valid) return;
  }

  if (ageGateOn) {
    const min = Math.max(18, Number(ageMin || 18));

    if (!Number.isFinite(min) || min < 18) {
      alert('Age min must be ≥ 18');
      return;
    }

    if (ageMax !== '') {
      const max = Number(ageMax);

      if (!Number.isFinite(max) || max < min) {
        alert('Age max must be ≥ min');
        return;
      }
    }
  }

  let computedRevealAtMinutes = null;

  if (eventType === 'reveal') {
    if (!eventDate || !startTime12 || !endTime12) {
      alert('Set Event Date, Start, and End times first.');
      return;
    }

    if (!revealTimeISO) {
      alert('Pick a Reveal time.');
      return;
    }

    const hasIcebreaker =
      Array.isArray(icebreakers) &&
      icebreakers.some(q => String(q || '').trim());

    if (!hasIcebreaker) {
      alert('Please add at least one icebreaker question for Reveal events.');
      return;
    }

    const s24 = convertTo24Hour(startTime12);

    if (eventDate && s24 && revealTimeISO) {
      const startMs = localDate(eventDate, s24).getTime();
      const revealMs = new Date(revealTimeISO).getTime();

      computedRevealAtMinutes = Math.max(
        0,
        Math.round((revealMs - startMs) / 60000)
      );
    }
  }

  if (
    eventType === 'mixer' &&
    purposeTag === 'Dating' &&
    orientationType === 'heterosexual'
  ) {
    const cap = Number(capacity) || 0;
    const m = Number(maleCap) || 0;
    const f = Number(femaleCap) || 0;

    if (cap > 0 && m + f !== cap) {
      alert('For a heterosexual mixer with a max capacity, Men + Women spots must exactly equal total capacity.');
      return;
    }

    const times = (roundStartTimes || []).filter(Boolean);

    const promptsOk =
      Array.isArray(roundPrompts) &&
      times.length > 0 &&
      times.length === roundPrompts.length &&
      roundPrompts.every(p => String(p || '').trim());

    if (!promptsOk) {
      alert('Please add a custom icebreaker question for each round (one prompt per scheduled round).');
      return;
    }
  }

  if (
    eventType === 'matchmaking' &&
    purposeTag === 'Dating' &&
    classicOrientation === 'heterosexual'
  ) {
    const cap = Number(capacity) || 0;
    const m = Number(maleCap) || 0;
    const f = Number(femaleCap) || 0;

    if (cap > 0 && m + f !== cap) {
      alert('For a heterosexual classic event with a max capacity, Men + Women spots must exactly equal total capacity.');
      return;
    }
  }

  try {
    const token = localStorage.getItem('adminToken') || '';

    const base = {
      name,
      address,
      latitude,
      longitude,
      radius,
      description,
      agendaPdfUrl,
      agendaPdfPublicId,
      agendaPdfName,
      eventDate: eventDate.trim() || '',
      startTime12: startTime12.trim() || '',
      endTime12: endTime12.trim() || '',
      hostedAt: hostedAt.trim() || '',
      promoterId: selectedPromoterId || null,
      capacity: capacity === '' ? 0 : Number(capacity),
      mode: eventType,
      whereToMeetSpot: whereToMeetSpot.trim(),
      whereToMeetNote: whereToMeetNote.trim(),
      whereToMeetImageUrl: whereToMeetImageUrl.trim(),
      ageGateOn: !!ageGateOn,
      ageMin: Math.max(18, Number(ageMin || 18)),
      ageMax: ageMax === '' ? null : Number(ageMax),
    };

    const cleanTags = Array.from(
      new Set((tags || []).map(t => String(t).toLowerCase().trim()))
    );

    const payload =
      eventType === 'mixer'
        ? {
            name,
            eventDate: eventDate.trim(),
            startTime12: startTime12.trim(),
            endTime12: endTime12.trim(),
            venue: hostedAt.trim(),
            hostedAt: hostedAt.trim(),
            address: address.trim(),
            latitude: latitude === '' ? 0 : Number(latitude),
            longitude: longitude === '' ? 0 : Number(longitude),
            radius: radius === '' ? 0 : Number(radius),
            capacity: capacity === '' ? 0 : Number(capacity),

            traitAxes:
              purposeTag === 'Dating' && orientationType === 'heterosexual'
                ? roundStartTimes.filter(Boolean).map((_, i) => `Round ${i + 1}`)
                : roundTraits,

            quizLen: Number(quizLength),
            promoterId: selectedPromoterId || null,
            vibeTag: vibeTag || null,
            mode: 'mixer',
            description: description.trim() || '',
            purposeTag: purposeTag.trim() || '',

            orientationType: purposeTag === 'Dating' ? orientationType || '' : '',
            genderGate:
              purposeTag === 'Friendship' || purposeTag === 'Networking'
                ? genderGate || 'all'
                : 'all',

            affinity: !!affinity,

            maleCap:
              purposeTag === 'Dating' &&
              orientationType === 'heterosexual' &&
              Number(capacity) > 0
                ? Number(maleCap || 0)
                : 0,

            femaleCap:
              purposeTag === 'Dating' &&
              orientationType === 'heterosexual' &&
              Number(capacity) > 0
                ? Number(femaleCap || 0)
                : 0,

            useAutoOccupancy,
            galleryImages: galleryList,
            savedUserIds: [],
            occupancyCount: Number(occupancyCount) || 0,
            attendees: [],

            roundStartTimes: roundStartTimes
              .filter(Boolean)
              .map(t => new Date(t).toISOString()),

            roundPrompts,
            price: Number(price) || 0,
            clothing: clothing.trim(),
            venueType: venueType.trim(),

            ageGateOn: !!ageGateOn,
            ageMin: Math.max(18, Number(ageMin || 18)),
            ageMax: ageMax === '' ? null : Number(ageMax),
            tags: cleanTags,
          }
        : eventType === 'reveal'
        ? {
            ...base,
            occupancyCount: Number(occupancyCount) || 0,
            galleryImages: galleryList,
            useAutoOccupancy,
            maxGroupSize: Number(maxGroupSize) || 2,
            purposeTag: purposeTag.trim() || '',
            icebreakers,
            price: Number(price) || 0,
            clothing: clothing.trim(),
            venueType: venueType.trim(),
            tags: cleanTags,
            affinity: !!affinity,

            revealMode: {
              orientation: reveal.orientation,
              revealAtMinutes: Number(
                computedRevealAtMinutes != null
                  ? computedRevealAtMinutes
                  : reveal?.revealAtMinutes || 0
              ),
            },
          }
        : eventType === 'neutral'
        ? {
            ...base,
            mode: 'neutral',
            galleryImages: galleryList,
            navigationImages: navList,
            useAutoOccupancy,
            maxGroupSize: Number(maxGroupSize) || 2,
            purposeTag: purposeTag.trim() || '',
            icebreakers,
            price: Number(price) || 0,
            clothing: clothing.trim(),
            venueType: venueType.trim(),
            tags: cleanTags,
            neutralGenderGate,
            neutralOrientation,
            neutralMaleCap: Number(neutralMaleCap || 0),
            neutralFemaleCap: Number(neutralFemaleCap || 0),
            affinity: !!affinity,
          }
        : {
            ...base,
            occupancyCount: Number(occupancyCount) || 0,
            galleryImages: galleryList,
            navigationImages: navList,
            navigationSpotDescription: '',
            useAutoOccupancy,
            maxGroupSize: Number(maxGroupSize) || 2,
            purposeTag: purposeTag.trim() || '',
            icebreakers,
            price: Number(price) || 0,
            clothing: clothing.trim(),
            venueType: venueType.trim(),
            tags: cleanTags,

            classicGenderGate:
              purposeTag === 'Friendship' || purposeTag === 'Networking'
                ? classicGenderGate
                : 'all',

            classicOrientation:
              purposeTag === 'Dating'
                ? classicOrientation === 'bisexual'
                  ? 'open'
                  : classicOrientation
                : 'open',

            affinity: !!affinity,

            maleCap:
              purposeTag === 'Dating' &&
              classicOrientation === 'heterosexual' &&
              Number(capacity) > 0
                ? Number(maleCap || 0)
                : 0,

            femaleCap:
              purposeTag === 'Dating' &&
              classicOrientation === 'heterosexual' &&
              Number(capacity) > 0
                ? Number(femaleCap || 0)
                : 0,
          };

    const prevReveal = {
      eventDate: editEvent?.eventDate,
      startTime12: editEvent?.startTime12,
      revealAtMinutes: Number(editEvent?.revealMode?.revealAtMinutes ?? -1),
    };

    const nextReveal = {
      eventDate,
      startTime12,
      revealAtMinutes: Number(
        eventType === 'reveal'
          ? computedRevealAtMinutes != null
            ? computedRevealAtMinutes
            : reveal?.revealAtMinutes ?? -1
          : -1
      ),
    };

if (eventType !== 'mixer') {
  Object.assign(payload, getMarketLocationPayload());
}

    if (payload.mode === 'neutral') {
      payload.radius = 5280;
    }

    const url =
      eventType === 'mixer'
        ? `${process.env.REACT_APP_API_URL}/mixer/events/${editEvent._id}`
        : `${process.env.REACT_APP_API_URL}/admin/events/${editEvent._id}`;

    await axios.put(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (eventType !== 'mixer') {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/events/${editEvent._id}/urgency-settings`,
        {
          soldOutOverride,
          displayAttendeeCountOverride:
            displayAttendeeCountOverride === ''
              ? null
              : Number(displayAttendeeCountOverride),
          displayCapacityOverride:
            displayCapacityOverride === ''
              ? null
              : Number(displayCapacityOverride),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    }

    if (
      eventType === 'reveal' &&
      revealFieldsChanged(prevReveal, nextReveal)
    ) {
      await callRevealReset(editEvent._id, { cleanup: true });
    }

    alert('Event updated');
    setShowEditForm(false);
    setEditEvent(null);
    clearForm();
    fetchEvents();
  } catch (err) {
    alert(err.response?.data?.message || err.message);
  }
};


// ------------------------------------------------------------
// DELETE EVENT
// ------------------------------------------------------------
const handleDelete = async (eventId, mode) => {
  if (!window.confirm('Are you sure you want to delete this event?')) return;

  try {
    const token = localStorage.getItem('adminToken') || '';

    const url =
      mode === 'mixer'
        ? `${process.env.REACT_APP_API_URL}/mixer/events/${eventId}`
        : `${process.env.REACT_APP_API_URL}/admin/events/${eventId}`;

    await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    alert('Event deleted');
    fetchEvents();
  } catch (err) {
    alert(err.response?.data?.message || err.message);
  }
};


// ------------------------------------------------------------
// CLEAR FORM
// ------------------------------------------------------------
const clearForm = () => {
  setResolvedLocation(null);
setLocationBusy(false);
  setName('');
  setAddress('');
  setLatitude('');
  setLongitude('');
  setRadius('5280');
  setDescription('');
  setOccupancyCount('');

  setWhereToMeetSpot('');
  setWhereToMeetNote('');
  setWhereToMeetImageUrl('');
  setWhereToMeetUploading(false);

  setUseAutoOccupancy(true);
  setMaxGroupSize('2');
  setPurposeTag('');

  setMaleCap('');
  setFemaleCap('');
  setOrientationType('');
  setGenderGate('all');
  setAffinity(false);

  setClassicGenderGate('all');
  setClassicOrientation('open');

  setNeutralGenderGate('all');
  setNeutralOrientation('open');
  setNeutralMaleCap('');
  setNeutralFemaleCap('');
  setNeutralLaneCapsOn(false);

  setEventDate('');
  setStartTime12('');
  setEndTime12('');
  setHostedAt('');

  setGalleryList([]);

  setAgendaPdfUrl('');
  setAgendaPdfPublicId('');
  setAgendaPdfName('');
  setAgendaPdfUploading(false);

  setNavList([]);
  setNavUrl('');
  setNavDesc('');

  setEditEvent(null);

  setPrice('20');
  setClothing('');
  setVenueType('');
  setVibeTag('');
  setTags([]);

  setSelectedPromoterId('');
  setPromoterMenuOpen(false);

  setAgeGateOn(false);
  setAgeMin('18');
  setAgeMax('');

  setIcebreakers([]);
  setNewIcebreaker('');
  setSelectedPreset('');

  setEventType('matchmaking');
  setQuizLength(3);
  setRoundTraits([]);
  setRoundStartTimes([]);
  setRoundPrompts([]);

  setReveal({
    orientation: 'open',
    revealAtMinutes: 20,
  });

  setRevealTimeISO('');
};


// ------------------------------------------------------------
// GALLERY / PDF / MEETING IMAGE
// ------------------------------------------------------------
async function uploadOneGalleryImage(file) {
  const api = process.env.REACT_APP_API_URL;
  const form = new FormData();

  form.append('image', file);

  const token =
    localStorage.getItem('adminToken') ||
    localStorage.getItem('accessToken') ||
    '';

  const res = await axios.post(`${api}/events/gallery-image`, form, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'multipart/form-data',
    },
  });

  const url = res.data?.url;

  if (!url) throw new Error('Upload failed: no url returned');

  return url;
}


async function uploadAgendaPdf(file) {
  const api = process.env.REACT_APP_API_URL;
  const form = new FormData();

  form.append('pdf', file);

  const token =
    localStorage.getItem('adminToken') ||
    localStorage.getItem('accessToken') ||
    '';

  const res = await axios.post(`${api}/events/agenda-pdf`, form, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'multipart/form-data',
    },
  });

  const url = res.data?.url;

  if (!url) {
    throw new Error('Upload failed: no PDF URL returned');
  }

  return {
    url,
    publicId: res.data?.publicId || '',
    name: res.data?.name || file.name || '',
  };
}


async function handleAgendaPdfSelected(e) {
  const file = e.target.files?.[0];
  e.target.value = '';

  if (!file) return;

  const isPdf =
    file.type === 'application/pdf' ||
    String(file.name || '').toLowerCase().endsWith('.pdf');

  if (!isPdf) {
    alert('Please select a PDF file.');
    return;
  }

  setAgendaPdfUploading(true);

  try {
    const uploaded = await uploadAgendaPdf(file);

    setAgendaPdfUrl(uploaded.url);
    setAgendaPdfPublicId(uploaded.publicId);
    setAgendaPdfName(uploaded.name);
  } catch (err) {
    alert(
      err?.response?.data?.message ||
        err.message ||
        'Agenda PDF upload failed'
    );
  } finally {
    setAgendaPdfUploading(false);
  }
}


function removeAgendaPdf() {
  setAgendaPdfUrl('');
  setAgendaPdfPublicId('');
  setAgendaPdfName('');
}


async function handleWhereToMeetImageSelected(e) {
  const file = e.target.files?.[0];
  e.target.value = '';

  if (!file) return;

  setWhereToMeetUploading(true);

  try {
    const url = await uploadOneGalleryImage(file);
    setWhereToMeetImageUrl(url);
  } catch (err) {
    alert(err?.response?.data?.message || err.message || 'Upload failed');
  } finally {
    setWhereToMeetUploading(false);
  }
}


function removeWhereToMeetImage() {
  setWhereToMeetImageUrl('');
}


async function handleGalleryFilesSelected(e) {
  const files = Array.from(e.target.files || []);
  e.target.value = '';

  if (!files.length) return;

  const remaining = Math.max(0, 3 - (galleryList?.length || 0));

  if (remaining <= 0) {
    alert('You can only upload up to 3 gallery images.');
    return;
  }

  setGalleryUploading(true);

  try {
    for (const file of files.slice(0, remaining)) {
      const url = await uploadOneGalleryImage(file);

      setGalleryList(prev => {
        const next = Array.isArray(prev) ? [...prev] : [];

        if (!next.includes(url)) next.push(url);

        return next.slice(0, 3);
      });
    }
  } catch (err) {
    alert(err?.response?.data?.message || err.message || 'Upload failed');
  } finally {
    setGalleryUploading(false);
  }
}


function removeGalleryAt(idx) {
  setGalleryList(prev => {
    const copy = [...(prev || [])];
    copy.splice(idx, 1);
    return copy;
  });
}


// ------------------------------------------------------------
// NAV IMAGES
// ------------------------------------------------------------
const handleAddNavImage = () => {
  if (!navUrl.trim()) return;

  setNavList([
    ...navList,
    {
      url: navUrl.trim(),
      description: navDesc.trim() || '',
    },
  ]);

  setNavUrl('');
  setNavDesc('');
};


const handleRemoveNavImage = (idx) => {
  const copy = [...navList];
  copy.splice(idx, 1);
  setNavList(copy);
};


// ------------------------------------------------------------
// ICEBREAKERS
// ------------------------------------------------------------
function handleAddIcebreaker() {
  if (!newIcebreaker.trim()) return;

  setIcebreakers([...icebreakers, newIcebreaker.trim()]);
  setNewIcebreaker('');
}


function handleRemoveIcebreaker(idx) {
  const copy = [...icebreakers];
  copy.splice(idx, 1);
  setIcebreakers(copy);
}


function handlePresetChange(e) {
  setSelectedPreset(e.target.value);
}


function loadPresetIntoIcebreakers() {
  if (!selectedPreset) return;

  const presetArray = ICEBREAKER_PRESETS[selectedPreset];
  if (!presetArray) return;

  if (
    window.confirm(
      `Replace current icebreakers with the "${selectedPreset}" preset?`
    )
  ) {
    setIcebreakers([...presetArray]);
  }
}


// ------------------------------------------------------------
// BOUNCER CODE
// ------------------------------------------------------------
async function generateOrRotateBouncerCode(eventId) {
  try {
    const token = localStorage.getItem('adminToken') || '';

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/admin/events/${eventId}/bouncer-code`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (res.status === 200 && res.data?.code) {
      setIssuedCodes(prev => ({
        ...prev,
        [eventId]: res.data.code,
      }));
    } else {
      alert(res.data?.message || 'Failed to set Bouncer Code');
    }
  } catch (err) {
    console.error('[bouncer-code] ->', err);
    alert(err.response?.data?.message || err.message);
  }
}


async function revealBouncerCode(eventId) {
  try {
    const token = localStorage.getItem('adminToken') || '';

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/admin/events/${eventId}/bouncer-code`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (res.status === 200 && res.data?.code) {
      setIssuedCodes(prev => ({
        ...prev,
        [eventId]: res.data.code,
      }));
    } else {
      alert(res.data?.message || 'No code set');
    }
  } catch (err) {
    alert(err.response?.data?.message || err.message);
  }
}


// ------------------------------------------------------------
// SAVE AN EXISTING EVENT AS A TEMPLATE
// ------------------------------------------------------------
async function handleSaveAsTemplate(sourceEvent) {
  if (!sourceEvent?._id) return;

  const eventId = String(sourceEvent._id);

  try {
    setSavingTemplateId(eventId);

    const token = localStorage.getItem('adminToken') || '';

    await axios.post(
      `${process.env.REACT_APP_API_URL}/admin/event-templates`,
      {
        name: sourceEvent.name,
        mode: sourceEvent.mode || 'matchmaking',
        eventData: sourceEvent,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setTemplateRefreshKey(key => key + 1);
    window.alert(`"${sourceEvent.name}" saved as a template.`);
  } catch (err) {
    console.error(
      '[handleSaveAsTemplate]',
      err?.response?.data || err.message
    );

    window.alert(
      err?.response?.data?.message ||
        'Failed to save template.'
    );
  } finally {
    setSavingTemplateId('');
  }
}


// ------------------------------------------------------------
// USE TEMPLATE IN THE FULL ADD EVENT FORM
// ------------------------------------------------------------
function handleUseTemplate(template) {
  const templateData = {
    ...(template?.eventData || {}),
    name:
      template?.eventData?.name ||
      template?.name ||
      '',
    mode:
      template?.mode ||
      template?.eventData?.mode ||
      'matchmaking',

    // A template should never carry these choices into a new event.
    eventDate: '',
    promoterId: null,
  };

  clearForm();
  handleEditClick(templateData);

  // handleEditClick fills every field. These final calls switch that
  // populated state from the edit flow into the normal create flow.
  setEditEvent(null);
  setCreatingTemplate(false);
  setShowEditForm(false);
  setShowAddForm(true);

  window.scrollTo({ top: 0, behavior: 'smooth' });
}


// ------------------------------------------------------------
// RENDER EVENTS
// ------------------------------------------------------------
function renderEventsSection() {
  const liveUpcoming = (events || []).filter(e => !isPastEvent(e));
  const livePast = (events || []).filter(e => isPastEvent(e));

 return (
  <div>
    <button
      type="button"
      onClick={() => setShowMarketsModal(true)}
      style={{ marginRight: 10, marginBottom: 14 }}
    >
       Manage markets
    </button>

  <AdminMarketsModal
  open={showMarketsModal}
  onClose={() => setShowMarketsModal(false)}
  onChanged={fetchEvents}
/>

    {!showAddForm && !showEditForm && (
      <button
          onClick={() => {
            clearForm();
            setCreatingTemplate(false);
            setShowAddForm(true);
          }}
        >
          Add New Event
        </button>
      )}


      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 10,
          marginBottom: 14,
          width: '100%',
        }}
      >
        <div style={{ fontWeight: 900 }}>View:</div>

        <select
          value={eventsView}
          onChange={e => setEventsView(e.target.value)}
        >
          <option value="main">Main page</option>
          <option value="past_live">Past live events</option>
        </select>
      </div>

      
      {/* ADD FORM */}
{showAddForm && !showEditForm && (
  <form
    onSubmit={handleAddEvent}
    style={{ border: '1px solid #ccc', padding: 10, marginTop: 10 }}
  >
    <h3>{creatingTemplate ? 'Add Template' : 'Add Event'}</h3>

    {/* Basic Fields */}
    <div>
      <label>Name: </label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
    </div>

    <div>
      <label>Hosted At (Venue Name): </label>
      <input
        value={hostedAt}
        onChange={(e) => setHostedAt(e.target.value)}
      />
    </div>

    {eventType === 'mixer' ? (
      <>
        <div>
          <label>Address: </label>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        <div>
          <label>Latitude: </label>
          <input
            type="text"
            value={latitude}
            onChange={(e) => setLatitude(e.target.value)}
          />
        </div>

        <div>
          <label>Longitude: </label>
          <input
            type="text"
            value={longitude}
            onChange={(e) => setLongitude(e.target.value)}
          />
        </div>
      </>
    ) : (
      <AdminEventLocation
        address={address}
        onAddressChange={setAddress}
        value={resolvedLocation}
        onResolved={handleLocationResolved}
        onBusyChange={setLocationBusy}
      />
    )}

    <div>
      <label>Radius (feet): </label>
      <input
        value={radius}
        onChange={(e) => {
          if (eventType !== 'neutral') {
            setRadius(e.target.value);
          }
        }}
      />
    </div>

    <div>
      <label>Description: </label>
      <br />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
      />
    </div>

<div style={{ marginTop: 12, padding: 10, border: '1px solid #e5e7eb', borderRadius: 10 }}>
  <h4 style={{ margin: '0 0 10px' }}>Where to meet</h4>

  <div style={{ marginTop: 8 }}>
    <label>Meeting spot headline/subheading:</label>
    <br />
    <input
      type="text"
      value={whereToMeetSpot}
      onChange={(e) => setWhereToMeetSpot(e.target.value)}
      placeholder="Near the front entrance by the patio."
      style={{ width: '100%', maxWidth: 520 }}
    />
  </div>

  <div style={{ marginTop: 8 }}>
    <label>Meeting note:</label>
    <br />
    <textarea
      value={whereToMeetNote}
      onChange={(e) => setWhereToMeetNote(e.target.value)}
      rows={2}
      placeholder="Look for your host or check the group chat for updates when they arrive."
      style={{ width: '100%', maxWidth: 520 }}
    />
  </div>

  <div style={{ marginTop: 10 }}>
    <label>Meeting spot image:</label>
    <br />
    <input
      type="file"
      accept="image/*"
      disabled={whereToMeetUploading}
      onChange={handleWhereToMeetImageSelected}
    />

    <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
      {whereToMeetUploading ? 'Uploading…' : 'Upload one image that helps users find the group.'}
    </div>

    {whereToMeetImageUrl && (
      <div style={{ marginTop: 10, width: 180 }}>
        <img
          src={whereToMeetImageUrl}
          alt="Where to meet"
          style={{
            width: 180,
            height: 120,
            objectFit: 'cover',
            borderRadius: 10,
            border: '1px solid #e5e7eb',
          }}
        />

        <button
          type="button"
          onClick={removeWhereToMeetImage}
          style={{ marginTop: 6, width: '100%' }}
          disabled={whereToMeetUploading}
        >
          Remove
        </button>
      </div>
    )}
  </div>
</div>

       {eventType !== 'neutral' && (
  <div>
    <label>Occupancy Count: </label>
    <input
      type="number"
      value={occupancyCount}
      onChange={(e) => setOccupancyCount(e.target.value)}
    />
  </div>
)}

<div style={{ marginTop: 8 }}>
  <label>Vibe:</label>{' '}

  <select
    value={vibeTag}
    onChange={(e) =>
      setVibeTag(e.target.value)
    }
  >
    <option value="">(select)</option>
    <option value="Chill">
      Chill
    </option>
    <option value="Night Out">
      Night Out
    </option>
    <option value="Adventure">
      Adventure
    </option>
  </select>
</div>

            {/* ─── Venue & dress‑code selectors ───────────────────────── */}
<div style={{ marginTop: 8 }}>
  <label>Venue type:</label>{' '}
  <select
    value={venueType}
    onChange={e => setVenueType(e.target.value)}
  >
    <option value="">(select)</option>
    <option value="Bar">Bar</option>
    <option value="Restaurant">Restaurant</option>
    <option value="Cafe">Cafe</option>
    <option value="Park">Park</option>
    <option value="Museum">Museum</option>
    <option value="Outdoor">Outdoor</option>
    <option value="Hotel">Hotel</option>
    <option value="House">House</option>
    <option value="Other">Other</option>
  </select>
</div>

<div style={{ marginTop: 8 }}>
  <label>Dress code:</label>{' '}
  <select
    value={clothing}
    onChange={e => setClothing(e.target.value)}
  >
    <option value="">(select)</option>
    <option value="White Tie">White tie</option>
    <option value="Black Tie">Black tie</option>
    <option value="Formal">Formal</option>
    <option value="Semi-Formal">Semi-formal</option>
    <option value="Cocktail">Cocktail</option>
    <option value="Business Formal">Business formal</option>
    <option value="Business Casual">Business casual</option>
    <option value="Smart Casual">Smart casual</option>
    <option value="Casual">Casual</option>
    <option value="Themed">Themed</option>
  </select>
</div>

{/* === MAX CAPACITY (optional) =============================== */}
<div style={{ marginTop: 8 }}>
  <label>Max Attendees:</label>{' '}
  <select
    value={capacity === 0 ? 'unlimited' : 'custom'}
    onChange={(e) => {
      const choice = e.target.value;
      if (choice === 'unlimited') {
        setCapacity(0);
        // Clear lane caps when there is no max capacity
        setMaleCap('');
        setFemaleCap('');
      } else if (capacity === 0) {
        setCapacity('');
      }
    }}
    
  >
    <option value="unlimited">No max</option>
    <option value="custom">Custom…</option>
  </select>

  {/* show number box only if “Custom…” is chosen */}
  {capacity !== 0 && (
    <input
      type="number"
      min="1"
      style={{ marginLeft: 8, width: 80 }}
      value={capacity}
      onChange={(e) => setCapacity(e.target.value)}
    />
  )}
</div>

{/* === AGE GATE ============================================ */}
<div style={{ marginTop: 8, borderTop: '1px dashed #ddd', paddingTop: 8 }}>
  <label>
    <input
      type="checkbox"
      checked={ageGateOn}
      onChange={e => {
        const on = e.target.checked;
        setAgeGateOn(on);
        if (on && (Number(ageMin || 0) < 18)) setAgeMin('18');
      }}
    />{' '}
    Enable age gate
  </label>

  {ageGateOn && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
      <div>
        <label>Min:</label>{' '}
        <input
          type="number"
          min="18"
          value={ageMin}
          onChange={e => {
            const v = e.target.value;
            if (v === '') { setAgeMin('18'); return; }
            const n = Math.max(18, Number(v));
            setAgeMin(String(n));
            if (ageMax !== '' && Number(ageMax) < n) setAgeMax(String(n));
          }}
          style={{ width: 80 }}
        />
      </div>
      <div>
        <label>Max (empty = no max):</label>{' '}
        <input
          type="number"
          min={ageMin || 18}
          value={ageMax}
          onChange={e => {
            const v = e.target.value;
            if (v === '') { setAgeMax(''); return; }
            const n = Math.max(Number(ageMin || 18), Number(v));
            setAgeMax(String(n));
          }}
          style={{ width: 110 }}
        />
      </div>
      <div style={{ fontSize: 12, color: '#666' }}>
        {ageMax === ''
          ? `${ageMin}+`
          : (Number(ageMin) === Number(ageMax) ? `${ageMin} only` : `${ageMin}–${ageMax}`)}
      </div>
    </div>
  )}
</div>



            <div>
              <label>Use Auto-Occupancy? </label>
              
              <input
                type="checkbox"
                checked={useAutoOccupancy}
                onChange={(e) => setUseAutoOccupancy(e.target.checked)}
              />
            </div>
            <div style={{ marginTop: 8 }}>
  <label>Event Price ($): </label>
  <input
    type="number"
    min="0"
    step="0.01"
    value={price}
    onChange={(e) => setPrice(e.target.value)}
  />
</div>
     {/* === Max-Group-Size (NOT for Neutral or Mixer) ============== */}
{(eventType === 'matchmaking' || eventType === 'reveal') && (
  <div style={{ marginTop: 8 }}>
    <label>Max Group Size: </label>
    <select
      value={maxGroupSize}
      onChange={(e) => setMaxGroupSize(e.target.value)}
    >
      <option value="2">2</option>
      <option value="3">3</option>
    </select>
  </div>
)}

            
        {/* ---------- MODE SELECTOR (edit form) ---------- */}
        <div style={{ marginTop: 8 }}>
  <label>Event Mode:</label>{' '}
  <select
    value={eventType}
    onChange={e => setEventType(e.target.value)}
  >
    <option value="matchmaking">Match-Making (classic)</option>
    <option value="neutral">Neutral (group chat)</option>
    <option value="mixer">Color-Match Mixer</option>
    <option value="reveal">Reveal Mode</option>
  </select>
</div>
{/* ---------- NEUTRAL AUDIENCE (ALWAYS SHOW) ---------- */}
{eventType === 'neutral' && (
  <div style={{ marginTop: 10, padding: 10, border: '1px dashed #888' }}>
    <h4>Neutral Audience</h4>

    {/* Gender gate */}
    <div style={{ marginTop: 8 }}>
      <label>Gender gate:</label>{' '}
      <select
        value={neutralGenderGate}
onChange={e => applyNeutralGateAndOrientation(e.target.value, neutralOrientation)}
      >
        <option value="all">All</option>
        <option value="men_only">Men only</option>
        <option value="women_only">Women only</option>
      </select>
    </div>

    {/* Sexual orientation */}
    <div style={{ marginTop: 8 }}>
      <label>Sexual orientation:</label>{' '}
      <select
        value={neutralOrientation}
onChange={e => applyNeutralGateAndOrientation(neutralGenderGate, e.target.value)}
      >
      <option value="open">Open to all</option>
<option value="lgbtq">LGBTQ+</option>
<option value="straight">Heterosexual</option>
<option value="gay_men">Gay Men</option>
<option value="gay_women">Gay Women</option>
      </select>
    </div>

    <div style={{ marginTop: 10 }}>
  <label style={{ fontWeight: 700 }}>
    <input
      type="checkbox"
      checked={neutralLaneCapsOn}
      onChange={(e) => setNeutralLaneCapsOn(e.target.checked)}
      disabled={
        neutralCapNum <= 0 ||
        neutralIsGaySingleGender ||
        neutralIsSingleGenderGate
      }
      style={{ marginRight: 8 }}
    />
    Enable lane caps (Men / Women)
  </label>

  {(neutralCapNum <= 0 || neutralIsGaySingleGender || neutralIsSingleGenderGate) && (
    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
      {neutralCapNum <= 0
        ? 'Set a max attendee count to enable lane caps.'
        : neutralIsSingleGenderGate
        ? 'Lane caps are not available when Gender gate is Men only / Women only.'
        : 'Lane caps are not available for Gay Men / Gay Women orientation labels.'}
    </div>
  )}
</div>

{/* Lane caps (Neutral) */}
<div
  style={{
    marginTop: 10,
    padding: 10,
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    opacity: neutralLaneCapsDisabled ? 0.45 : 1,
    pointerEvents: neutralLaneCapsDisabled ? 'none' : 'auto',
  }}
>
  <div style={{ fontWeight: 800, marginBottom: 6 }}>Lane caps</div>

  {neutralLaneCapsDisabled ? (
  <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.35 }}>
    {!neutralLaneCapsOn
      ? 'Turn on “Enable lane caps” to set Men/Women spots.'
      : neutralCapNum <= 0
      ? 'Set a max attendee count to enable lane caps.'
      : neutralIsSingleGenderGate
      ? 'Lane caps are not available when Gender gate is Men only / Women only.'
      : 'Lane caps are not available for Gay Men / Gay Women orientation labels.'}
  </div>
) : (

    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div>
          <label>Men’s spots</label>
          <input
            type="number"
            min="0"
            value={neutralMaleCap}
            onChange={(e) => setNeutralMaleCap(e.target.value)}
            style={{ width: 100, marginLeft: 8 }}
          />
        </div>
        <div>
          <label>Women’s spots</label>
          <input
            type="number"
            min="0"
            value={neutralFemaleCap}
            onChange={(e) => setNeutralFemaleCap(e.target.value)}
            style={{ width: 120, marginLeft: 8 }}
          />
        </div>
        <div style={{ fontSize: 12, color: '#111' }}>
          Total: <b>{neutralMaleCapNum + neutralFemaleCapNum}</b> / {neutralCapNum}
        </div>
      </div>

      {!neutralLaneCapsValid && (
        <div style={{ marginTop: 8, color: '#B91C1C', fontSize: 12, fontWeight: 800 }}>
          Men’s spots + Women’s spots must equal capacity ({neutralCapNum}).
        </div>
      )}
    </>
  )}
</div>


    {/* Affinity */}
    <div style={{ marginTop: 8 }}>
      <label>
        <input
          type="checkbox"
          checked={affinity}
          onChange={e => setAffinity(e.target.checked)}
        />{' '}
        Affinity audience
      </label>
    </div>
  </div>
)}


{eventType === 'reveal' && (
  <div style={{ marginTop: 10, padding: 10, border: '1px dashed #888' }}>
    <h4>Reveal Settings</h4>
{/* ───────────────── Icebreakers (Reveal) ───────────────── */}
{eventType === 'reveal' && (
  <div style={{ marginTop: 10, border: '1px solid #ddd', padding: 8 }}>
    <h4>Icebreakers (Reveal)</h4>

    <p style={{ margin: 0, fontSize: '0.85rem', color: '#777' }}>
      Add one or more prompts. We’ll randomly pick one for each in-match pair.
    </p>

    <ul style={{ marginTop: 8 }}>
      {icebreakers.map((q, idx) => (
        <li key={idx} style={{ marginBottom: 4 }}>
          {q}{' '}
          <button
            type="button"
            onClick={() => {
              const copy = [...icebreakers];
              copy.splice(idx, 1);
              setIcebreakers(copy);
            }}
          >
            Remove
          </button>
        </li>
      ))}
    </ul>

    <div style={{ marginTop: 6 }}>
      <input
        type="text"
        placeholder="Add new question"
        value={newIcebreaker}
        onChange={(e) => setNewIcebreaker(e.target.value)}
        style={{ marginRight: 8 }}
      />
      <button
        type="button"
        onClick={() => {
          if (!newIcebreaker.trim()) return;
          setIcebreakers([...icebreakers, newIcebreaker.trim()]);
          setNewIcebreaker('');
        }}
      >
        Add
      </button>
    </div>
  </div>
)}

    <div style={{ marginTop: 8 }}>
      <label>
        <input
          type="checkbox"
          checked={affinity}
          onChange={e => setAffinity(e.target.checked)}
        />{' '}
        Affinity audience
      </label>
    </div>

    {/* Orientation */}
    <div style={{ marginTop: 8 }}>
      <label>Orientation: </label>{' '}
      <select
  value={reveal.orientation}
  onChange={e => setReveal(r => ({ ...r, orientation: e.target.value }))}
>
  <option value="straight">Heterosexual</option>
  <option value="gay_men">Gay Men</option>
  <option value="gay_women">Gay Women</option>
  <option value="open">Open to all genders</option>
</select>

    </div>

    {/* Reveal time (between Start and End) */}
    <div style={{ marginTop: 8 }}>
      <label>Reveal time:</label>{' '}
      {(() => {
        const s24 = convertTo24Hour(startTime12);
        const e24 = convertTo24Hour(endTime12);
        const minHHMM = hhmmFrom24h(s24);
        const maxHHMM = hhmmFrom24h(e24);
        const valueHHMM = revealTimeISO ? hhmmFromISO(revealTimeISO) : '';

        return (
          <input
            type="time"
            step="60"
            disabled={!eventDate || !s24 || !e24}
            min={minHHMM || undefined}
            max={maxHHMM || undefined}
            value={valueHHMM}
            onChange={(e) => {
              const hhmm = e.target.value; // "HH:MM"
              if (!hhmm) {
                setRevealTimeISO('');
                setReveal(r => ({ ...r, revealAtMinutes: 0 }));
                return;
              }
              const iso = isoFromDateAndHHMM(eventDate, hhmm);
              setRevealTimeISO(iso);

              if (s24) {
                const startMs  = localDate(eventDate, s24).getTime();
                const revealMs = new Date(iso).getTime();
                const diffMin  = Math.max(0, Math.round((revealMs - startMs) / 60000));
                setReveal(r => ({ ...r, revealAtMinutes: diffMin }));
              }
            }}
          />
        );
      })()}

      {revealTimeISO && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#555' }}>
          Selected:{' '}
          {(() => {
            const d = new Date(revealTimeISO);
            const mm = String(d.getMinutes()).padStart(2,'0');
            const h  = d.getHours();
            const ampm = h < 12 ? 'AM' : 'PM';
            const h12 = ((h + 11) % 12) + 1;
            return `${h12}:${mm} ${ampm}`;
          })()}
        </div>
      )}
    </div>
  </div>
)}


{/* ---------- CLASSIC-ONLY FIELDS ---------- */}
{eventType === 'matchmaking' && (
  <div style={{ marginTop: 10, padding: 10, border: '1px dashed #888' }}>
    <h4>Classic Audience</h4>

    {(purposeTag === 'Friendship' || purposeTag === 'Networking') && (
      <div style={{ marginTop: 8 }}>
        <label>Gender gate:</label>{' '}
        <select
          value={classicGenderGate}
          onChange={e => setClassicGenderGate(e.target.value)}
        >
          <option value="all">All</option>
          <option value="men_only">Men only</option>
          <option value="women_only">Women only</option>
        </select>
      </div>
    )}

    {purposeTag === 'Dating' && (
      <>
        <div style={{ marginTop: 8 }}>
          <label>Orientation:</label>{' '}
          <select
            value={classicOrientation}
            onChange={e => {
              const v = e.target.value;
              setClassicOrientation(v);
              if (v !== 'heterosexual') {
                setMaleCap('');
                setFemaleCap('');
              }
            }}
          >
            <option value="heterosexual">Heterosexual</option>
            <option value="gay_men">Gay Men</option>
            <option value="gay_women">Gay Women</option>
            <option value="open">Open to all</option> {/* bisexual merged */}
          </select>
        </div>

        {/* Hetero lane split (capacity > 0) */}
        {classicOrientation === 'heterosexual' && Number(capacity) > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'center' }}>
            <div>
              <label>Men’s spots</label>
              <input
                type="number"
                min="0"
                style={{ width: 90, marginLeft: 6 }}
                value={maleCap}
                onChange={e => setMaleCap(e.target.value)}
              />
            </div>
            <div>
              <label>Women’s spots</label>
              <input
                type="number"
                min="0"
                style={{ width: 110, marginLeft: 6 }}
                value={femaleCap}
                onChange={e => setFemaleCap(e.target.value)}
              />
            </div>
            {(Number(capacity) > 0) &&
              (Number(maleCap || 0) + Number(femaleCap || 0) !== Number(capacity || 0)) && (
                <span style={{ color: '#B91C1C', fontSize: 12, fontWeight: 700 }}>
                  Men + Women must equal capacity.
                </span>
            )}
          </div>
        )}
      </>
    )}

    {/* Affinity (checkbox only) */}
    <div style={{ marginTop: 8 }}>
      <label>
        <input
          type="checkbox"
          checked={affinity}
          onChange={e => setAffinity(e.target.checked)}
        />{' '}
        Affinity audience
      </label>
    </div>
  </div>
)}

{/* ---------- MIXER-ONLY FIELDS ---------- */}
{eventType === 'mixer' && (
  <div style={{ marginTop: 10, padding: 10, border: '1px dashed #888' }}>
    <h4>Color-Match Settings</h4>

    {/* Purpose-driven audience controls */}
    {purposeTag === 'Dating' ? (
      <>
        <div style={{ marginTop: 8 }}>
          <label>Orientation:</label>{' '}
          <select
            value={orientationType}
            onChange={e => {
              const v = e.target.value;
              setOrientationType(v);
              if (v !== 'heterosexual') {
                setMaleCap('');
                setFemaleCap('');
              }
            }}
          >
            <option value="">(select)</option>
            <option value="gay_women">Gay Women</option>
            <option value="heterosexual">Heterosexual</option>
            <option value="gay_men">Gay Men</option>
            <option value="gender_fluid">Open to all genders</option>
          </select>
        </div>

        {/* Hetero lane split (capacity > 0) */}
        {orientationType === 'heterosexual' && Number(capacity) > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'center' }}>
            <div>
              <label>Men’s spots</label>
              <input
                type="number"
                min="0"
                style={{ width: 90, marginLeft: 6 }}
                value={maleCap}
                onChange={e => setMaleCap(e.target.value)}
              />
            </div>
            <div>
              <label>Women’s spots</label>
              <input
                type="number"
                min="0"
                style={{ width: 110, marginLeft: 6 }}
                value={femaleCap}
                onChange={e => setFemaleCap(e.target.value)}
              />
            </div>
            {Number(capacity) > 0 &&
              (Number(maleCap || 0) + Number(femaleCap || 0)) !== Number(capacity || 0) && (
                <span style={{ color: '#B91C1C', fontSize: 12, fontWeight: 700 }}>
                  Men + Women must equal capacity.
                </span>
              )}
          </div>
        )}
      </>
    ) : (purposeTag === 'Friendship' || purposeTag === 'Networking') ? (
      <div style={{ marginTop: 8 }}>
        <label>Gender gate:</label>{' '}
        <select value={genderGate} onChange={e => setGenderGate(e.target.value)}>
          <option value="men_only">Men only</option>
          <option value="women_only">Women only</option>
          <option value="all">All</option>
        </select>
      </div>
    ) : null}

    {/* Affinity audience */}
    <div style={{ marginTop: 8 }}>
      <label>
        <input
          type="checkbox"
          checked={affinity}
          onChange={e => setAffinity(e.target.checked)}
        />{' '}
        Affinity audience
      </label>
    </div>

    {/* Quiz length (hide when hetero dating lanes are active) */}
    {!(purposeTag === 'Dating' && orientationType === 'heterosexual') && (
      <div style={{ marginTop: 8 }}>
        <label>Quiz items / trait:</label>{' '}
        <input
          type="number"
          min="2"
          max="7"
          style={{ width: 60 }}
          value={quizLength}
          onChange={e => setQuizLength(e.target.value)}
        />
      </div>
    )}



    {/* Dynamic Round Order and Start Times */}
   {/* Round Traits and Start Times */}
<div style={{ marginTop: 10 }}>
  <label>Round Order and Start Times:</label>

  {roundTraits.map((trait, idx) => (
    <div key={idx} style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
      <span style={{ width: 22 }}>{idx + 1}.</span>

     {/* Inside the map over rounds */}
     {eventType === 'mixer' && purposeTag === 'Dating' && orientationType === 'heterosexual' ? (
  // Hetero: show a fixed label instead of trait select
  <div style={{ flex: 1, marginRight: 8, fontWeight: 600 }}>
    {`Round ${idx + 1}`}
  </div>
) : (
  <select
    style={{ flex: 1, marginRight: 8 }}
    value={trait}
    onChange={(e) => {
      const updatedTraits = [...roundTraits];
      updatedTraits[idx] = e.target.value;
      setRoundTraits(updatedTraits);
    }}
  >
    <option value="">— choose trait —</option>
    {TRAIT_AXES.map((axis) => (
      <option key={axis} value={axis}>{axis}</option>
    ))}
  </select>
)}

      <select
  value={
    roundStartTimes[idx]
      ? (() => {
          const d = new Date(roundStartTimes[idx]);
          const mins = d.getMinutes().toString().padStart(2, '0');
          const hours24 = d.getHours();
          const ampm = hours24 < 12 ? 'AM' : 'PM';
          const hours12 = ((hours24 + 11) % 12) + 1;
          return `${hours12}:${mins} ${ampm}`;
        })()
      : ''
  }
  onChange={e => {
    const sel = e.target.value;
    if (!sel) return;
  
    const [time, ampm] = sel.split(' ');
    let [h, m] = time.split(':').map(Number);
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
  
    // Convert clearly to UTC string
    const localDate = new Date(`${eventDate}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
    const localISOString = `${eventDate}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
  
    setRoundStartTimes(prev => {
      const copy = [...prev];
      copy[idx] = localISOString;
      return copy;
    });    
  }}  
>


  <option value="">Select Time</option>
  {generateTimeOptions(eventDate, startTime12, endTime12, roundStartTimes, idx).map((time) => (
    <option
      key={time.value}
      value={time.value}
      disabled={time.disabled}
      style={{ color: time.disabled ? '#ccc' : '#000' }}
    >
      {time.label}
    </option>
  ))}
</select>



      <button
        type="button"
        style={{ marginLeft: 8 }}
        onClick={() => {
          setRoundTraits(roundTraits.filter((_, i) => i !== idx));
          setRoundStartTimes(roundStartTimes.filter((_, i) => i !== idx));
          setRoundPrompts(roundPrompts.filter((_, i) => i !== idx));
        }}
      >
        Remove
      </button>
    </div>
  ))}

  <button
    type="button"
    style={{ marginTop: 6 }}
    onClick={() => {
      setRoundTraits([...roundTraits, '']);
      setRoundStartTimes([...roundStartTimes, '']);
      setRoundPrompts([...roundPrompts, '']);
    }}
  >
    + Add Round
  </button>
</div>

  </div>
)}



            {/* Single day + Start/End Times (12-hour) */}
            <div style={{ marginTop: 8 }}>
              <label>Event Date: </label>
              <input
  type="date"
  value={eventDate}
  onChange={e => handleDateChange(e.target.value)}
/>
            </div>
            <div style={{ marginTop: 8 }}>
              <label>Start Time (12-hr): </label>
             {/* Start Time (12-hr) */}
<input
  type="text"
  placeholder="08:00 AM"
  value={startTime12}
  onChange={(e) => {
    setStartTime12(e.target.value);
    if (eventType === 'reveal') setRevealTimeISO('');
  }}
/>

{/* End Time (12-hr) */}
<input
  type="text"
  placeholder="01:30 PM"
  value={endTime12}
  onChange={(e) => {
    setEndTime12(e.target.value);
    if (eventType === 'reveal') setRevealTimeISO('');
  }}
/>

            </div>

{/* Purpose Tag */}
        <div style={{ marginTop: 8 }}>
  <label>Purpose Tag: </label>
  <select
    value={purposeTag}
    onChange={e => setPurposeTag(e.target.value)}
  >
    <option value="">(none)</option>
    <option value="Friendship">Friendship</option>
    <option value="Dating">Dating</option>
    <option value="Networking">Networking</option>
  </select>
</div>
{/* Event Tags (multi-select, powers discovery bubbles) */}
<div style={{ marginTop: 8 }}>
  <label style={{ display: 'block', fontWeight: 800, marginBottom: 6 }}>
    Event Tags
  </label>
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
    {TAG_OPTIONS.map(opt => {
      const active = tags.includes(opt.id);
      return (
        <label
          key={opt.id}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            borderRadius: 999,
            border: active ? '2px solid #0b0b0b' : '1px solid #e5e7eb',
            background: active ? '#f8fafc' : '#fff',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => {
              const on = e.target.checked;
              setTags(prev => on
                ? [...new Set([...prev, opt.id])]
                : prev.filter(t => t !== opt.id)
              );
            }}
            style={{ accentColor: '#0b0b0b' }}
          />
          <span style={{ fontWeight: 700, fontSize: 13 }}>{opt.label}</span>
        </label>
      );
    })}
  </div>
  <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
    Selected: {tags.length ? tags.join(', ') : '(none)'}
  </div>
</div>

{/* 👇 Place it here for clarity and good grouping */}
{/* PROMOTER (rich selector) */}
<div style={{ marginTop: 12, position: 'relative' }}>
  <label style={{ display: 'block', fontWeight: 800, marginBottom: 6 }}>
    Promoter
  </label>

  {/* Selected button */}
  <button
    type="button"
    onClick={() => setPromoterMenuOpen(v => !v)}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      padding: '10px 12px',
      borderRadius: 10,
      border: '1px solid #e5e7eb',
      background: '#fff',
      cursor: 'pointer',
      textAlign: 'left',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: '#f3f4f6',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {selectedPromoter?.imageUrl ? (
          <img
            src={selectedPromoter.imageUrl}
            alt={selectedPromoter.name || 'Promoter'}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : null}
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 900, fontSize: 14, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selectedPromoter ? (selectedPromoter.name || 'Unnamed promoter') : 'Select a promoter'}
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selectedPromoter
            ? (selectedPromoter.email || '') 
            : 'No promoter selected'}
        </div>
        {selectedPromoter ? (
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {tenureLabelFrom(selectedPromoter.hostSince || selectedPromoter.createdAt)}
          </div>
        ) : null}
      </div>
    </div>

    <div style={{ fontSize: 12, fontWeight: 800, color: '#111' }}>
      {promoterMenuOpen ? 'Close' : 'Change'}
    </div>
  </button>

  {/* Dropdown menu */}
  {promoterMenuOpen && (
    <div
      style={{
        position: 'absolute',
        zIndex: 50,
        top: 'calc(100% + 8px)',
        left: 0,
        right: 0,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        maxHeight: 320,
        overflowY: 'auto',
      }}
    >
      {/* Clear option */}
      <button
        type="button"
        onClick={() => {
          setSelectedPromoterId('');
          setPromoterMenuOpen(false);
        }}
        style={{
          width: '100%',
          padding: 12,
          border: 'none',
          background: '#fff',
          cursor: 'pointer',
          textAlign: 'left',
          borderBottom: '1px solid #f3f4f6',
          fontWeight: 800,
        }}
      >
        No promoter
      </button>

      {(promoters || []).map((p) => {
        const active = String(p._id) === String(selectedPromoterId);
        return (
          <button
            key={p._id}
            type="button"
            onClick={() => {
              setSelectedPromoterId(String(p._id));
              setPromoterMenuOpen(false);
            }}
            style={{
              width: '100%',
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              padding: 12,
              border: 'none',
              background: active ? '#f8fafc' : '#fff',
              cursor: 'pointer',
              textAlign: 'left',
              borderBottom: '1px solid #f3f4f6',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: '#f3f4f6',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {p?.imageUrl ? (
                <img
                  src={p.imageUrl}
                  alt={p.name || 'Promoter'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : null}
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 900, fontSize: 14, color: '#111' }}>
                {p.name || 'Unnamed promoter'}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {p.email || ''}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {tenureLabelFrom(p.hostSince || p.createdAt)}
              </div>
            </div>

            {active ? (
              <div style={{ marginLeft: 'auto', fontWeight: 900, color: '#111' }}>
                Selected
              </div>
            ) : null}
          </button>
        );
      })}
    </div>
  )}
</div>


         {/* GALLERY (UPLOAD ONLY; max 3) */}
<div style={{ marginTop: 10 }}>
  <h4>Gallery Images</h4>

  <input
    type="file"
    accept="image/*"
    multiple
    disabled={galleryUploading || (galleryList?.length || 0) >= 3}
    onChange={handleGalleryFilesSelected}
  />

  <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
    {galleryUploading ? 'Uploading…' : `Up to 3 images. (${galleryList.length}/3)`}
  </div>

  <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
    {(galleryList || []).map((url, idx) => (
      <div key={url} style={{ width: 110 }}>
        <img
          src={url}
          alt={`Gallery ${idx + 1}`}
          style={{
            width: 110,
            height: 110,
            objectFit: 'cover',
            borderRadius: 10,
            border: '1px solid #e5e7eb',
          }}
        />
        <button
          type="button"
          onClick={() => removeGalleryAt(idx)}
          style={{ marginTop: 6, width: '100%' }}
          disabled={galleryUploading}
        >
          Remove
        </button>
      </div>
    ))}
  </div>
</div>

{/* HOST AGENDA PDF */}
<div
  style={{
    marginTop: 10,
    padding: 10,
    border: '1px solid #e5e7eb',
    borderRadius: 10,
  }}
>
  <h4 style={{ margin: '0 0 10px' }}>
    Host Agenda PDF
  </h4>

  <input
    type="file"
    accept="application/pdf,.pdf"
    disabled={agendaPdfUploading}
    onChange={handleAgendaPdfSelected}
  />

  <div
    style={{
      marginTop: 6,
      fontSize: 12,
      color: '#6b7280',
    }}
  >
    {agendaPdfUploading
      ? 'Uploading…'
      : 'Upload one PDF for the host agenda.'}
  </div>

  {agendaPdfUrl && (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        {agendaPdfName || 'Attached agenda PDF'}
      </div>

      <a
        href={agendaPdfUrl}
        target="_blank"
        rel="noreferrer"
        style={{ marginRight: 10 }}
      >
        View PDF
      </a>

      <button
        type="button"
        onClick={removeAgendaPdf}
        disabled={agendaPdfUploading}
      >
        Remove
      </button>
    </div>
  )}
</div>

            {/* NAV IMAGES */}
         {/* === Navigation Images – classic events only =============== */}
         {eventType === 'matchmaking' && (
  <div style={{ marginTop: 10 }}>
    <h4>Navigation Images (URL + Description)</h4>

    <label>Photo URL:</label>
    <br />
    <input
      type="text"
      value={navUrl}
      onChange={(e) => setNavUrl(e.target.value)}
    />
    <br />

    <label>Description:</label>
    <br />
    <input
      type="text"
      value={navDesc}
      onChange={(e) => setNavDesc(e.target.value)}
    />

    <button type="button" onClick={handleAddNavImage}>
      Add Navigation Photo
    </button>

    <ul>
      {navList.map((item, idx) => (
        <li key={idx}>
          <b>URL:</b> {item.url} | <b>Desc:</b> {item.description}{' '}
          <button
            type="button"
            onClick={() => handleRemoveNavImage(idx)}
          >
            Remove
          </button>
        </li>
      ))}
    </ul>
  </div>
)}


          {/* ICEBREAKERS + PRESETS — show only for “Match-Making (classic)” */}
          {eventType === 'matchmaking' && (
  <div style={{ marginTop: 10, border: '1px solid #ddd', padding: 8 }}>
    <h4>Icebreakers</h4>

    <p style={{ margin: 0, fontSize: '0.85rem', color: '#777' }}>
      Select a preset to replace the list, or add your own custom Qs:
    </p>

    <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
      <select
        style={{ marginRight: 8 }}
        value={selectedPreset}
        onChange={handlePresetChange}
      >
        <option value="">(No Preset)</option>
        <option value="dating">Dating</option>
        <option value="friendship">Friendship</option>
        <option value="networking">Networking</option>
      </select>

      <button type="button" onClick={loadPresetIntoIcebreakers}>
        Load Preset
      </button>
    </div>

    <ul style={{ marginTop: 8 }}>
      {icebreakers.map((q, idx) => (
        <li key={idx} style={{ marginBottom: 4 }}>
          {q}{' '}
          <button type="button" onClick={() => handleRemoveIcebreaker(idx)}>
            Remove
          </button>
        </li>
      ))}
    </ul>

    <div style={{ marginTop: 6 }}>
      <input
        type="text"
        placeholder="Add new question"
        value={newIcebreaker}
        onChange={(e) => setNewIcebreaker(e.target.value)}
        style={{ marginRight: 8 }}
      />
      <button type="button" onClick={handleAddIcebreaker}>
        Add
      </button>
    </div>
  </div>
)}

{/* ---- Save / Cancel ---- */}
<div style={{ marginTop: 10 }}>
<button type="submit">
  {creatingTemplate ? 'Save Template' : 'Publish'}
</button>
  <button
    type="button"
    onClick={() => {
      /* same handler you already use to close the form */
      setShowAddForm ? setShowAddForm(false) : setShowEditForm(false);
      if (setEditEvent) setEditEvent(null);
      setCreatingTemplate(false);
      clearForm();
    }}
    style={{ marginLeft: 8 }}
  >
    Cancel
  </button>
</div>
          </form>
        )}

   {/* EDIT FORM */}
{showEditForm && !showAddForm && (
  <form
    onSubmit={handleEditEvent}
    style={{ border: '1px solid #ccc', padding: 10, marginTop: 10 }}
  >
    <h3>Edit Event</h3>

    {/* Basic Fields */}
    <div>
      <label>Name: </label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
    </div>

    <div>
      <label>Hosted At (Venue Name): </label>
      <input
        value={hostedAt}
        onChange={(e) => setHostedAt(e.target.value)}
      />
    </div>

    {eventType === 'mixer' ? (
      <>
        <div>
          <label>Address: </label>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        <div>
          <label>Latitude: </label>
          <input
            type="text"
            value={latitude}
            onChange={(e) => setLatitude(e.target.value)}
          />
        </div>

        <div>
          <label>Longitude: </label>
          <input
            type="text"
            value={longitude}
            onChange={(e) => setLongitude(e.target.value)}
          />
        </div>
      </>
    ) : (
      <AdminEventLocation
        address={address}
        onAddressChange={setAddress}
        value={resolvedLocation}
        onResolved={handleLocationResolved}
        onBusyChange={setLocationBusy}
      />
    )}

    <div>
      <label>Radius (feet): </label>
      <input
        value={radius}
        onChange={(e) => {
          if (eventType !== 'neutral') {
            setRadius(e.target.value);
          }
        }}
      />
    </div>

    <div>
      <label>Description: </label>
      <br />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
      />
    </div>

    <div>
      <div
        style={{
          marginTop: 12,
          padding: 10,
          border: '1px solid #e5e7eb',
          borderRadius: 10,
        }}
      >
        <h4 style={{ margin: '0 0 10px' }}>Where to meet</h4>

        <div style={{ marginTop: 8 }}>
          <label>Meeting spot headline/subheading:</label>
          <br />
          <input
            type="text"
            value={whereToMeetSpot}
            onChange={(e) => setWhereToMeetSpot(e.target.value)}
            placeholder="Near the front entrance by the patio."
            style={{ width: '100%', maxWidth: 520 }}
          />
        </div>

        <div style={{ marginTop: 8 }}>
          <label>Meeting note:</label>
          <br />
          <textarea
            value={whereToMeetNote}
            onChange={(e) => setWhereToMeetNote(e.target.value)}
            rows={2}
            placeholder="Look for your host or check the group chat for updates when they arrive."
            style={{ width: '100%', maxWidth: 520 }}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <label>Meeting spot image:</label>
          <br />
          <input
            type="file"
            accept="image/*"
            disabled={whereToMeetUploading}
            onChange={handleWhereToMeetImageSelected}
          />

          <div
            style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}
          >
            {whereToMeetUploading
              ? 'Uploading…'
              : 'Upload one image that helps users find the group.'}
          </div>

          {whereToMeetImageUrl && (
            <div style={{ marginTop: 10, width: 180 }}>
              <img
                src={whereToMeetImageUrl}
                alt="Where to meet"
                style={{
                  width: 180,
                  height: 120,
                  objectFit: 'cover',
                  borderRadius: 10,
                  border: '1px solid #e5e7eb',
                }}
              />

              <button
                type="button"
                onClick={removeWhereToMeetImage}
                style={{ marginTop: 6, width: '100%' }}
                disabled={whereToMeetUploading}
              >
                Remove
              </button>
            </div>
          )}
        </div>
      </div>

      <label>Occupancy Count: </label>
      <input
        type="number"
        value={occupancyCount}
        onChange={(e) => setOccupancyCount(e.target.value)}
      />
    </div>

    <div style={{ marginTop: 8 }}>
      <label>Vibe:</label>{' '}
      <select
        value={vibeTag}
        onChange={(e) => setVibeTag(e.target.value)}
      >
        <option value="">(select)</option>
        <option value="Chill">Chill</option>
        <option value="Night Out">Night Out</option>
        <option value="Adventure">Adventure</option>
      </select>
    </div>

    {/* Venue & dress-code selectors */}
    <div style={{ marginTop: 8 }}>
      <label>Venue type:</label>{' '}
      <select
        value={venueType}
        onChange={(e) => setVenueType(e.target.value)}
      >
        <option value="">(select)</option>
        <option value="Bar">Bar</option>
        <option value="Restaurant">Restaurant</option>
        <option value="Cafe">Cafe</option>
        <option value="Park">Park</option>
        <option value="Museum">Museum</option>
        <option value="Outdoor">Outdoor</option>
        <option value="Hotel">Hotel</option>
        <option value="House">House</option>
        <option value="Other">Other</option>
      </select>
    </div>

    <div style={{ marginTop: 8 }}>
      <label>Dress code:</label>{' '}
      <select
        value={clothing}
        onChange={(e) => setClothing(e.target.value)}
      >
        <option value="">(select)</option>
        <option value="White Tie">White tie</option>
        <option value="Black Tie">Black tie</option>
        <option value="Formal">Formal</option>
        <option value="Semi-Formal">Semi-formal</option>
        <option value="Cocktail">Cocktail</option>
        <option value="Business Formal">Business formal</option>
        <option value="Business Casual">Business casual</option>
        <option value="Smart Casual">Smart casual</option>
        <option value="Casual">Casual</option>
        <option value="Themed">Themed</option>
      </select>
    </div>

    {/* MAX CAPACITY */}
    <div style={{ marginTop: 8 }}>
      <label>Max Attendees:</label>{' '}
      <select
        value={capacity === 0 ? 'unlimited' : 'custom'}
        onChange={(e) => {
          const choice = e.target.value;

          if (choice === 'unlimited') {
            setCapacity(0);
            setMaleCap('');
            setFemaleCap('');
          } else if (capacity === 0) {
            setCapacity('');
          }
        }}
      >
        <option value="unlimited">No max</option>
        <option value="custom">Custom…</option>
      </select>

      {capacity !== 0 && (
        <input
          type="number"
          min="1"
          style={{ marginLeft: 8, width: 80 }}
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
        />
      )}
    </div>

    {eventType !== 'mixer' && (
      <div
        style={{
          marginTop: 12,
          padding: 12,
          border: '1px solid #e5e7eb',
          borderRadius: 10,
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: 10 }}>
          Urgency Controls
        </h4>

        <label style={{ display: 'block', marginBottom: 10 }}>
          <input
            type="checkbox"
            checked={soldOutOverride}
            onChange={(e) => setSoldOutOverride(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Force sold out
        </label>

        <div style={{ marginBottom: 8 }}>
          <label>Displayed attendee count:</label>
          <input
            type="number"
            min="0"
            value={displayAttendeeCountOverride}
            onChange={(e) =>
              setDisplayAttendeeCountOverride(e.target.value)
            }
            style={{ marginLeft: 8, width: 100 }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <label>Displayed capacity:</label>
          <input
            type="number"
            min="0"
            value={displayCapacityOverride}
            onChange={(e) =>
              setDisplayCapacityOverride(e.target.value)
            }
            style={{ marginLeft: 8, width: 100 }}
          />
        </div>

        <div
          style={{ fontSize: 12, color: '#666', marginBottom: 10 }}
        >
          Actual attendees:{' '}
          {editEvent?.attendeeCount ??
            editEvent?.attendees?.length ??
            0}
          {' '}• Actual capacity: {editEvent?.capacity ?? 0}
        </div>

        <button
          type="button"
          onClick={async () => {
            try {
              const token =
                localStorage.getItem('adminToken') || '';

              await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/events/${editEvent._id}/urgency-settings/reset`,
                {},
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              setSoldOutOverride(false);
              setDisplayAttendeeCountOverride('');
              setDisplayCapacityOverride('');
            } catch (err) {
              alert(err.response?.data?.message || err.message);
            }
          }}
        >
          Reset to Actual Numbers
        </button>
      </div>
    )}

    {/* AGE GATE */}
    <div
      style={{
        marginTop: 8,
        borderTop: '1px dashed #ddd',
        paddingTop: 8,
      }}
    >
      <label>
        <input
          type="checkbox"
          checked={ageGateOn}
          onChange={(e) => {
            const on = e.target.checked;
            setAgeGateOn(on);

            if (on && Number(ageMin || 0) < 18) {
              setAgeMin('18');
            }
          }}
        />{' '}
        Enable age gate
      </label>

      {ageGateOn && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 6,
          }}
        >
          <div>
            <label>Min:</label>{' '}
            <input
              type="number"
              min="18"
              value={ageMin}
              onChange={(e) => {
                const v = e.target.value;

                if (v === '') {
                  setAgeMin('18');
                  return;
                }

                const n = Math.max(18, Number(v));
                setAgeMin(String(n));

                if (ageMax !== '' && Number(ageMax) < n) {
                  setAgeMax(String(n));
                }
              }}
              style={{ width: 80 }}
            />
          </div>

          <div>
            <label>Max (empty = no max):</label>{' '}
            <input
              type="number"
              min={ageMin || 18}
              value={ageMax}
              onChange={(e) => {
                const v = e.target.value;

                if (v === '') {
                  setAgeMax('');
                  return;
                }

                const n = Math.max(
                  Number(ageMin || 18),
                  Number(v)
                );

                setAgeMax(String(n));
              }}
              style={{ width: 110 }}
            />
          </div>

          <div style={{ fontSize: 12, color: '#666' }}>
            {ageMax === ''
              ? `${ageMin}+`
              : Number(ageMin) === Number(ageMax)
                ? `${ageMin} only`
                : `${ageMin}–${ageMax}`}
          </div>
        </div>
      )}
    </div>

    <div>
      <label>Use Auto-Occupancy? </label>
      <input
        type="checkbox"
        checked={useAutoOccupancy}
        onChange={(e) => setUseAutoOccupancy(e.target.checked)}
      />
    </div>

    <div style={{ marginTop: 8 }}>
      <label>Event Price ($): </label>
      <input
        type="number"
        min="0"
        step="0.01"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
      />
    </div>

    {/* Max Group Size */}
    {(eventType === 'matchmaking' || eventType === 'reveal') && (
      <div style={{ marginTop: 8 }}>
        <label>Max Group Size: </label>
        <select
          value={maxGroupSize}
          onChange={(e) => setMaxGroupSize(e.target.value)}
        >
          <option value="2">2</option>
          <option value="3">3</option>
        </select>
      </div>
    )}

    <div style={{ marginTop: 8 }}>
      <label>Event Mode:</label>{' '}
      <select
        value={eventType}
        onChange={(e) => setEventType(e.target.value)}
      >
        <option value="matchmaking">Match-Making (classic)</option>
        <option value="neutral">Neutral (group chat)</option>
        <option value="mixer">Color-Match Mixer</option>
        <option value="reveal">Reveal Mode</option>
      </select>
    </div>

    {/* NEUTRAL AUDIENCE */}
    {eventType === 'neutral' && (
      <div
        style={{
          marginTop: 10,
          padding: 10,
          border: '1px dashed #888',
        }}
      >
        <h4>Neutral Audience</h4>

        <div style={{ marginTop: 8 }}>
          <label>Gender gate:</label>{' '}
          <select
            value={neutralGenderGate}
            onChange={(e) =>
              applyNeutralGateAndOrientation(
                e.target.value,
                neutralOrientation
              )
            }
          >
            <option value="all">All</option>
            <option value="men_only">Men only</option>
            <option value="women_only">Women only</option>
          </select>
        </div>

        <div style={{ marginTop: 8 }}>
          <label>Sexual orientation:</label>{' '}
          <select
            value={neutralOrientation}
            onChange={(e) =>
              applyNeutralGateAndOrientation(
                neutralGenderGate,
                e.target.value
              )
            }
          >
            <option value="open">Open to all</option>
            <option value="lgbtq">LGBTQ+</option>
            <option value="straight">Heterosexual</option>
            <option value="gay_men">Gay Men</option>
            <option value="gay_women">Gay Women</option>
          </select>
        </div>

        <div style={{ marginTop: 10 }}>
          <label style={{ fontWeight: 700 }}>
            <input
              type="checkbox"
              checked={neutralLaneCapsOn}
              onChange={(e) =>
                setNeutralLaneCapsOn(e.target.checked)
              }
              disabled={
                neutralCapNum <= 0 ||
                neutralIsGaySingleGender ||
                neutralIsSingleGenderGate
              }
              style={{ marginRight: 8 }}
            />
            Enable lane caps (Men / Women)
          </label>

          {(neutralCapNum <= 0 ||
            neutralIsGaySingleGender ||
            neutralIsSingleGenderGate) && (
            <div
              style={{
                fontSize: 12,
                color: '#6b7280',
                marginTop: 4,
              }}
            >
              {neutralCapNum <= 0
                ? 'Set a max attendee count to enable lane caps.'
                : neutralIsSingleGenderGate
                  ? 'Lane caps are not available when Gender gate is Men only / Women only.'
                  : 'Lane caps are not available for Gay Men / Gay Women orientation labels.'}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 10,
            padding: 10,
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            opacity: neutralLaneCapsDisabled ? 0.45 : 1,
            pointerEvents: neutralLaneCapsDisabled
              ? 'none'
              : 'auto',
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 6 }}>
            Lane caps
          </div>

          {neutralLaneCapsDisabled ? (
            <div
              style={{
                fontSize: 12,
                color: '#6b7280',
                lineHeight: 1.35,
              }}
            >
              {!neutralLaneCapsOn
                ? 'Turn on “Enable lane caps” to set Men/Women spots.'
                : neutralCapNum <= 0
                  ? 'Set a max attendee count to enable lane caps.'
                  : neutralIsSingleGenderGate
                    ? 'Lane caps are not available when Gender gate is Men only / Women only.'
                    : 'Lane caps are not available for Gay Men / Gay Women orientation labels.'}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <div>
                  <label>Men’s spots</label>
                  <input
                    type="number"
                    min="0"
                    value={neutralMaleCap}
                    onChange={(e) =>
                      setNeutralMaleCap(e.target.value)
                    }
                    style={{ width: 100, marginLeft: 8 }}
                  />
                </div>

                <div>
                  <label>Women’s spots</label>
                  <input
                    type="number"
                    min="0"
                    value={neutralFemaleCap}
                    onChange={(e) =>
                      setNeutralFemaleCap(e.target.value)
                    }
                    style={{ width: 120, marginLeft: 8 }}
                  />
                </div>

                <div style={{ fontSize: 12, color: '#111' }}>
                  Total:{' '}
                  <b>{neutralMaleCapNum + neutralFemaleCapNum}</b>
                  {' '}/ {neutralCapNum}
                </div>
              </div>

              {!neutralLaneCapsValid && (
                <div
                  style={{
                    marginTop: 8,
                    color: '#B91C1C',
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  Men’s spots + Women’s spots must equal capacity (
                  {neutralCapNum}).
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ marginTop: 8 }}>
          <label>
            <input
              type="checkbox"
              checked={affinity}
              onChange={(e) => setAffinity(e.target.checked)}
            />{' '}
            Affinity audience
          </label>
        </div>
      </div>
    )}

    {eventType === 'reveal' && (
      <div
        style={{
          marginTop: 10,
          padding: 10,
          border: '1px dashed #888',
        }}
      >
        <h4>Reveal Settings</h4>

        {/* Icebreakers (Reveal) */}
        {eventType === 'reveal' && (
          <div
            style={{
              marginTop: 10,
              border: '1px solid #ddd',
              padding: 8,
            }}
          >
            <h4>Icebreakers (Reveal)</h4>

            <p
              style={{
                margin: 0,
                fontSize: '0.85rem',
                color: '#777',
              }}
            >
              Add one or more prompts. We’ll randomly pick one for
              each in-match pair.
            </p>

            <ul style={{ marginTop: 8 }}>
              {icebreakers.map((q, idx) => (
                <li key={idx} style={{ marginBottom: 4 }}>
                  {q}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      const copy = [...icebreakers];
                      copy.splice(idx, 1);
                      setIcebreakers(copy);
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>

            <div style={{ marginTop: 6 }}>
              <input
                type="text"
                placeholder="Add new question"
                value={newIcebreaker}
                onChange={(e) => setNewIcebreaker(e.target.value)}
                style={{ marginRight: 8 }}
              />

              <button
                type="button"
                onClick={() => {
                  if (!newIcebreaker.trim()) return;

                  setIcebreakers([
                    ...icebreakers,
                    newIcebreaker.trim(),
                  ]);
                  setNewIcebreaker('');
                }}
              >
                Add
              </button>
            </div>
          </div>
        )}

        <div style={{ marginTop: 8 }}>
          <label>
            <input
              type="checkbox"
              checked={affinity}
              onChange={(e) => setAffinity(e.target.checked)}
            />{' '}
            Affinity audience
          </label>
        </div>

        <div style={{ marginTop: 8 }}>
          <label>Orientation: </label>{' '}
          <select
            value={reveal.orientation}
            onChange={(e) =>
              setReveal((r) => ({
                ...r,
                orientation: e.target.value,
              }))
            }
          >
            <option value="straight">Heterosexual</option>
            <option value="gay_men">Gay Men</option>
            <option value="gay_women">Gay Women</option>
            <option value="open">Open to all genders</option>
          </select>
        </div>

        <div style={{ marginTop: 8 }}>
          <label>Reveal time:</label>{' '}
          {(() => {
            const s24 = convertTo24Hour(startTime12);
            const e24 = convertTo24Hour(endTime12);
            const minHHMM = hhmmFrom24h(s24);
            const maxHHMM = hhmmFrom24h(e24);
            const valueHHMM = revealTimeISO
              ? hhmmFromISO(revealTimeISO)
              : '';

            return (
              <input
                type="time"
                step="60"
                disabled={!eventDate || !s24 || !e24}
                min={minHHMM || undefined}
                max={maxHHMM || undefined}
                value={valueHHMM}
                onChange={(e) => {
                  const hhmm = e.target.value;

                  if (!hhmm) {
                    setRevealTimeISO('');
                    setReveal((r) => ({
                      ...r,
                      revealAtMinutes: 0,
                    }));
                    return;
                  }

                  const iso = isoFromDateAndHHMM(
                    eventDate,
                    hhmm
                  );
                  setRevealTimeISO(iso);

                  if (s24) {
                    const startMs = localDate(
                      eventDate,
                      s24
                    ).getTime();

                    const revealMs = new Date(iso).getTime();
                    const diffMin = Math.max(
                      0,
                      Math.round((revealMs - startMs) / 60000)
                    );

                    setReveal((r) => ({
                      ...r,
                      revealAtMinutes: diffMin,
                    }));
                  }
                }}
              />
            );
          })()}

          {revealTimeISO && (
            <div
              style={{ marginTop: 6, fontSize: 12, color: '#555' }}
            >
              Selected:{' '}
              {(() => {
                const d = new Date(revealTimeISO);
                const mm = String(d.getMinutes()).padStart(
                  2,
                  '0'
                );
                const h = d.getHours();
                const ampm = h < 12 ? 'AM' : 'PM';
                const h12 = ((h + 11) % 12) + 1;

                return `${h12}:${mm} ${ampm}`;
              })()}
            </div>
          )}
        </div>
      </div>
    )}

    {/* CLASSIC-ONLY FIELDS */}
    {eventType === 'matchmaking' && (
      <div
        style={{
          marginTop: 10,
          padding: 10,
          border: '1px dashed #888',
        }}
      >
        <h4>Classic Audience</h4>

        {(purposeTag === 'Friendship' ||
          purposeTag === 'Networking') && (
          <div style={{ marginTop: 8 }}>
            <label>Gender gate:</label>{' '}
            <select
              value={classicGenderGate}
              onChange={(e) =>
                setClassicGenderGate(e.target.value)
              }
            >
              <option value="all">All</option>
              <option value="men_only">Men only</option>
              <option value="women_only">Women only</option>
            </select>
          </div>
        )}

        {purposeTag === 'Dating' && (
          <>
            <div style={{ marginTop: 8 }}>
              <label>Orientation:</label>{' '}
              <select
                value={classicOrientation}
                onChange={(e) => {
                  const v = e.target.value;
                  setClassicOrientation(v);

                  if (v !== 'heterosexual') {
                    setMaleCap('');
                    setFemaleCap('');
                  }
                }}
              >
                <option value="heterosexual">Heterosexual</option>
                <option value="gay_men">Gay Men</option>
                <option value="gay_women">Gay Women</option>
                <option value="open">Open to all</option>
              </select>
            </div>

            {classicOrientation === 'heterosexual' &&
              Number(capacity) > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <label>Men’s spots</label>
                    <input
                      type="number"
                      min="0"
                      style={{ width: 90, marginLeft: 6 }}
                      value={maleCap}
                      onChange={(e) => setMaleCap(e.target.value)}
                    />
                  </div>

                  <div>
                    <label>Women’s spots</label>
                    <input
                      type="number"
                      min="0"
                      style={{ width: 110, marginLeft: 6 }}
                      value={femaleCap}
                      onChange={(e) =>
                        setFemaleCap(e.target.value)
                      }
                    />
                  </div>

                  {Number(capacity) > 0 &&
                    Number(maleCap || 0) +
                      Number(femaleCap || 0) !==
                      Number(capacity || 0) && (
                      <span
                        style={{
                          color: '#B91C1C',
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Men + Women must equal capacity.
                      </span>
                    )}
                </div>
              )}
          </>
        )}

        <div style={{ marginTop: 8 }}>
          <label>
            <input
              type="checkbox"
              checked={affinity}
              onChange={(e) => setAffinity(e.target.checked)}
            />{' '}
            Affinity audience
          </label>
        </div>
      </div>
    )}

    {/* MIXER-ONLY FIELDS */}
    {eventType === 'mixer' && (
      <div
        style={{
          marginTop: 10,
          padding: 10,
          border: '1px dashed #888',
        }}
      >
        <h4>Color-Match Settings</h4>

        {purposeTag === 'Dating' ? (
          <>
            <div style={{ marginTop: 8 }}>
              <label>Orientation:</label>{' '}
              <select
                value={orientationType}
                onChange={(e) => {
                  const v = e.target.value;
                  setOrientationType(v);

                  if (v !== 'heterosexual') {
                    setMaleCap('');
                    setFemaleCap('');
                  }
                }}
              >
                <option value="">(select)</option>
                <option value="gay_women">Gay Women</option>
                <option value="heterosexual">Heterosexual</option>
                <option value="gay_men">Gay Men</option>
                <option value="gender_fluid">
                  Open to all genders
                </option>
              </select>
            </div>

            {orientationType === 'heterosexual' &&
              Number(capacity) > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <label>Men’s spots</label>
                    <input
                      type="number"
                      min="0"
                      style={{ width: 90, marginLeft: 6 }}
                      value={maleCap}
                      onChange={(e) => setMaleCap(e.target.value)}
                    />
                  </div>

                  <div>
                    <label>Women’s spots</label>
                    <input
                      type="number"
                      min="0"
                      style={{ width: 110, marginLeft: 6 }}
                      value={femaleCap}
                      onChange={(e) =>
                        setFemaleCap(e.target.value)
                      }
                    />
                  </div>

                  {Number(capacity) > 0 &&
                    Number(maleCap || 0) +
                      Number(femaleCap || 0) !==
                      Number(capacity || 0) && (
                      <span
                        style={{
                          color: '#B91C1C',
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Men + Women must equal capacity.
                      </span>
                    )}
                </div>
              )}
          </>
        ) : purposeTag === 'Friendship' ||
          purposeTag === 'Networking' ? (
          <div style={{ marginTop: 8 }}>
            <label>Gender gate:</label>{' '}
            <select
              value={genderGate}
              onChange={(e) => setGenderGate(e.target.value)}
            >
              <option value="men_only">Men only</option>
              <option value="women_only">Women only</option>
              <option value="all">All</option>
            </select>
          </div>
        ) : null}

        <div style={{ marginTop: 8 }}>
          <label>
            <input
              type="checkbox"
              checked={affinity}
              onChange={(e) => setAffinity(e.target.checked)}
            />{' '}
            Affinity audience
          </label>
        </div>

        {!(
          purposeTag === 'Dating' &&
          orientationType === 'heterosexual'
        ) && (
          <div style={{ marginTop: 8 }}>
            <label>Quiz items / trait:</label>{' '}
            <input
              type="number"
              min="2"
              max="7"
              style={{ width: 60 }}
              value={quizLength}
              onChange={(e) => setQuizLength(e.target.value)}
            />
          </div>
        )}

        {/* Dynamic Round Traits and Start Times */}
        <div style={{ marginTop: 10 }}>
          <label>Round Order and Start Times:</label>

          {roundTraits.map((trait, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 4,
              }}
            >
              <span style={{ width: 22 }}>{idx + 1}.</span>

              {eventType === 'mixer' &&
              purposeTag === 'Dating' &&
              orientationType === 'heterosexual' ? (
                <div
                  style={{
                    flex: 1,
                    marginRight: 8,
                    fontWeight: 600,
                  }}
                >
                  {`Round ${idx + 1}`}
                </div>
              ) : (
                <select
                  style={{ flex: 1, marginRight: 8 }}
                  value={trait}
                  onChange={(e) => {
                    const updatedTraits = [...roundTraits];
                    updatedTraits[idx] = e.target.value;
                    setRoundTraits(updatedTraits);
                  }}
                >
                  <option value="">— choose trait —</option>
                  {TRAIT_AXES.map((axis) => (
                    <option key={axis} value={axis}>
                      {axis}
                    </option>
                  ))}
                </select>
              )}

              <select
                value={
                  roundStartTimes[idx]
                    ? (() => {
                        const d = new Date(
                          roundStartTimes[idx]
                        );
                        const mins = d
                          .getMinutes()
                          .toString()
                          .padStart(2, '0');
                        const hours24 = d.getHours();
                        const ampm = hours24 < 12 ? 'AM' : 'PM';
                        const hours12 =
                          ((hours24 + 11) % 12) + 1;

                        return `${hours12}:${mins} ${ampm}`;
                      })()
                    : ''
                }
                onChange={(e) => {
                  const sel = e.target.value;
                  if (!sel) return;

                  const [time, ampm] = sel.split(' ');
                  let [h, m] = time.split(':').map(Number);

                  if (ampm === 'PM' && h !== 12) h += 12;
                  if (ampm === 'AM' && h === 12) h = 0;

                  const isoLocal =
                    `${eventDate}T` +
                    `${String(h).padStart(2, '0')}:` +
                    `${String(m).padStart(2, '0')}:00`;

                  setRoundStartTimes((prev) => {
                    const copy = [...prev];
                    copy[idx] = isoLocal;
                    return copy;
                  });
                }}
              >
                <option value="">Select Time</option>
                {generateTimeOptions(
                  eventDate,
                  startTime12,
                  endTime12,
                  roundStartTimes,
                  idx
                ).map((t) => (
                  <option
                    key={t.value}
                    value={t.value}
                    disabled={t.disabled}
                    style={{
                      color: t.disabled ? '#ccc' : '#000',
                    }}
                  >
                    {t.label}
                  </option>
                ))}
              </select>

              <input
                type="text"
                placeholder="Custom question for this round (optional)"
                value={roundPrompts[idx] || ''}
                onChange={(e) => {
                  const copy = [...roundPrompts];
                  copy[idx] = e.target.value;
                  setRoundPrompts(copy);
                }}
                style={{ flex: 2, minWidth: 260 }}
              />

              <button
                type="button"
                style={{ marginLeft: 8 }}
                onClick={() => {
                  setRoundTraits(
                    roundTraits.filter((_, i) => i !== idx)
                  );
                  setRoundStartTimes(
                    roundStartTimes.filter((_, i) => i !== idx)
                  );
                  setRoundPrompts(
                    roundPrompts.filter((_, i) => i !== idx)
                  );
                }}
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            style={{ marginTop: 6 }}
            onClick={() => {
              setRoundTraits([...roundTraits, '']);
              setRoundStartTimes([...roundStartTimes, '']);
              setRoundPrompts([...roundPrompts, '']);
            }}
          >
            + Add Round
          </button>
        </div>
      </div>
    )}

    {/* Single day + Start/End Times (12-hr) */}
    <div style={{ marginTop: 8 }}>
      <label>Event Date: </label>
      <input
        type="date"
        value={eventDate}
        onChange={(e) => handleDateChange(e.target.value)}
      />
    </div>

    <div style={{ marginTop: 8 }}>
      <label>Start Time (12-hr): </label>
      <input
        type="text"
        placeholder="08:00 AM"
        value={startTime12}
        onChange={(e) => {
          setStartTime12(e.target.value);
          if (eventType === 'reveal') setRevealTimeISO('');
        }}
      />

      <input
        type="text"
        placeholder="01:30 PM"
        value={endTime12}
        onChange={(e) => {
          setEndTime12(e.target.value);
          if (eventType === 'reveal') setRevealTimeISO('');
        }}
      />
    </div>

    {/* Purpose Tag */}
    <div style={{ marginTop: 8 }}>
      <label>Purpose Tag: </label>
      <select
        value={purposeTag}
        onChange={(e) => setPurposeTag(e.target.value)}
      >
        <option value="">(none)</option>
        <option value="Friendship">Friendship</option>
        <option value="Dating">Dating</option>
        <option value="Networking">Networking</option>
      </select>
    </div>

    {/* Event Tags */}
    <div style={{ marginTop: 8 }}>
      <label
        style={{
          display: 'block',
          fontWeight: 800,
          marginBottom: 6,
        }}
      >
        Event Tags
      </label>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        {TAG_OPTIONS.map((opt) => {
          const active = tags.includes(opt.id);

          return (
            <label
              key={opt.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 999,
                border: active
                  ? '2px solid #0b0b0b'
                  : '1px solid #e5e7eb',
                background: active ? '#f8fafc' : '#fff',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => {
                  const on = e.target.checked;

                  setTags((prev) =>
                    on
                      ? [...new Set([...prev, opt.id])]
                      : prev.filter((t) => t !== opt.id)
                  );
                }}
                style={{ accentColor: '#0b0b0b' }}
              />

              <span
                style={{ fontWeight: 700, fontSize: 13 }}
              >
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>

      <div
        style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}
      >
        Selected: {tags.length ? tags.join(', ') : '(none)'}
      </div>
    </div>

    {/* PROMOTER */}
    <div style={{ marginTop: 12, position: 'relative' }}>
      <label
        style={{
          display: 'block',
          fontWeight: 800,
          marginBottom: 6,
        }}
      >
        Promoter
      </label>

      <button
        type="button"
        onClick={() => setPromoterMenuOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '10px 12px',
          borderRadius: 10,
          border: '1px solid #e5e7eb',
          background: '#fff',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {selectedPromoter?.imageUrl ? (
              <img
                src={selectedPromoter.imageUrl}
                alt={selectedPromoter.name || 'Promoter'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : null}
          </div>

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 900,
                fontSize: 14,
                color: '#111',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {selectedPromoter
                ? selectedPromoter.name || 'Unnamed promoter'
                : 'Select a promoter'}
            </div>

            <div
              style={{
                fontSize: 12,
                color: '#6b7280',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {selectedPromoter
                ? selectedPromoter.email || ''
                : 'No promoter selected'}
            </div>

            {selectedPromoter ? (
              <div
                style={{ fontSize: 12, color: '#6b7280' }}
              >
                {tenureLabelFrom(
                  selectedPromoter.hostSince ||
                    selectedPromoter.createdAt
                )}
              </div>
            ) : null}
          </div>
        </div>

        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: '#111',
          }}
        >
          {promoterMenuOpen ? 'Close' : 'Change'}
        </div>
      </button>

      {promoterMenuOpen && (
        <div
          style={{
            position: 'absolute',
            zIndex: 50,
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setSelectedPromoterId('');
              setPromoterMenuOpen(false);
            }}
            style={{
              width: '100%',
              padding: 12,
              border: 'none',
              background: '#fff',
              cursor: 'pointer',
              textAlign: 'left',
              borderBottom: '1px solid #f3f4f6',
              fontWeight: 800,
            }}
          >
            No promoter
          </button>

          {(promoters || []).map((p) => {
            const active =
              String(p._id) === String(selectedPromoterId);

            return (
              <button
                key={p._id}
                type="button"
                onClick={() => {
                  setSelectedPromoterId(String(p._id));
                  setPromoterMenuOpen(false);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  padding: 12,
                  border: 'none',
                  background: active ? '#f8fafc' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  borderBottom: '1px solid #f3f4f6',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {p?.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt={p.name || 'Promoter'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : null}
                </div>

                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: 14,
                      color: '#111',
                    }}
                  >
                    {p.name || 'Unnamed promoter'}
                  </div>

                  <div
                    style={{ fontSize: 12, color: '#6b7280' }}
                  >
                    {p.email || ''}
                  </div>

                  <div
                    style={{ fontSize: 12, color: '#6b7280' }}
                  >
                    {tenureLabelFrom(
                      p.hostSince || p.createdAt
                    )}
                  </div>
                </div>

                {active ? (
                  <div
                    style={{
                      marginLeft: 'auto',
                      fontWeight: 900,
                      color: '#111',
                    }}
                  >
                    Selected
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>

    {/* GALLERY */}
    <div style={{ marginTop: 10 }}>
      <h4>Gallery Images</h4>

      <input
        type="file"
        accept="image/*"
        multiple
        disabled={
          galleryUploading || (galleryList?.length || 0) >= 3
        }
        onChange={handleGalleryFilesSelected}
      />

      <div
        style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}
      >
        {galleryUploading
          ? 'Uploading…'
          : `Up to 3 images. (${galleryList.length}/3)`}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 10,
          marginTop: 10,
          flexWrap: 'wrap',
        }}
      >
        {(galleryList || []).map((url, idx) => (
          <div key={url} style={{ width: 110 }}>
            <img
              src={url}
              alt={`Gallery ${idx + 1}`}
              style={{
                width: 110,
                height: 110,
                objectFit: 'cover',
                borderRadius: 10,
                border: '1px solid #e5e7eb',
              }}
            />

            <button
              type="button"
              onClick={() => removeGalleryAt(idx)}
              style={{ marginTop: 6, width: '100%' }}
              disabled={galleryUploading}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>

    {/* HOST AGENDA PDF */}
    <div
      style={{
        marginTop: 10,
        padding: 10,
        border: '1px solid #e5e7eb',
        borderRadius: 10,
      }}
    >
      <h4 style={{ margin: '0 0 10px' }}>Host Agenda PDF</h4>

      <input
        type="file"
        accept="application/pdf,.pdf"
        disabled={agendaPdfUploading}
        onChange={handleAgendaPdfSelected}
      />

      <div
        style={{
          marginTop: 6,
          fontSize: 12,
          color: '#6b7280',
        }}
      >
        {agendaPdfUploading
          ? 'Uploading…'
          : 'Upload one PDF for the host agenda.'}
      </div>

      {agendaPdfUrl && (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            {agendaPdfName || 'Attached agenda PDF'}
          </div>

          <a
            href={agendaPdfUrl}
            target="_blank"
            rel="noreferrer"
            style={{ marginRight: 10 }}
          >
            View PDF
          </a>

          <button
            type="button"
            onClick={removeAgendaPdf}
            disabled={agendaPdfUploading}
          >
            Remove
          </button>
        </div>
      )}
    </div>

    {/* Navigation Images — classic events only */}
    {eventType === 'matchmaking' && (
      <div style={{ marginTop: 10 }}>
        <h4>Navigation Images</h4>

        <label>Photo URL:</label>{' '}
        <input
          type="text"
          value={navUrl}
          onChange={(e) => setNavUrl(e.target.value)}
        />
        <br />

        <label>Description:</label>{' '}
        <input
          type="text"
          value={navDesc}
          onChange={(e) => setNavDesc(e.target.value)}
        />

        <button
          type="button"
          onClick={() => {
            if (!navUrl.trim()) return;

            const newItem = {
              url: navUrl.trim(),
              description: navDesc.trim() || '',
            };

            setNavList([...navList, newItem]);
            setNavUrl('');
            setNavDesc('');
          }}
        >
          Add Navigation Photo
        </button>

        <ul>
          {navList.map((item, idx) => (
            <li key={idx}>
              <b>URL:</b> {item.url} | <b>Desc:</b>{' '}
              {item.description}{' '}
              <button
                type="button"
                onClick={() => {
                  const copy = [...navList];
                  copy.splice(idx, 1);
                  setNavList(copy);
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Icebreakers — classic events only */}
    {eventType === 'matchmaking' && (
      <div
        style={{
          marginTop: 10,
          border: '1px solid #ddd',
          padding: 8,
        }}
      >
        <h4>Icebreakers</h4>

        <p
          style={{
            margin: 0,
            fontSize: '0.85rem',
            color: '#777',
          }}
        >
          Select a preset to replace the list, or add your own
          custom Qs:
        </p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: 4,
          }}
        >
          <select
            style={{ marginRight: 8 }}
            value={selectedPreset}
            onChange={handlePresetChange}
          >
            <option value="">(No Preset)</option>
            <option value="dating">Dating</option>
            <option value="friendship">Friendship</option>
            <option value="networking">Networking</option>
          </select>

          <button
            type="button"
            onClick={loadPresetIntoIcebreakers}
          >
            Load Preset
          </button>
        </div>

        <ul style={{ marginTop: 8 }}>
          {icebreakers.map((q, idx) => (
            <li key={idx} style={{ marginBottom: 4 }}>
              {q}{' '}
              <button
                type="button"
                onClick={() => handleRemoveIcebreaker(idx)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>

        <div style={{ marginTop: 6 }}>
          <input
            type="text"
            placeholder="Add new question"
            value={newIcebreaker}
            onChange={(e) => setNewIcebreaker(e.target.value)}
            style={{ marginRight: 8 }}
          />

          <button
            type="button"
            onClick={handleAddIcebreaker}
          >
            Add
          </button>
        </div>
      </div>
    )}

    <div style={{ marginTop: 10 }}>
      <button type="submit">Update</button>

      <button
        type="button"
        onClick={() => {
          setShowEditForm(false);
          setEditEvent(null);
          clearForm();
        }}
        style={{ marginLeft: 8 }}
      >
        Cancel
      </button>
    </div>
  </form>
)}
  {/* =========================
    EVENT LISTS
========================= */}

{eventsView === 'main' && (
  <>
    <AdminEventTemplates
      onUseTemplate={handleUseTemplate}
      onAddTemplate={() => {
        clearForm();
        setCreatingTemplate(true);
        setShowEditForm(false);
        setShowAddForm(true);
      }}
      refreshKey={templateRefreshKey}
    />

    <h3>Upcoming Events</h3>

    {liveUpcoming.length > 0 ? (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
        <thead>
          <tr style={{ backgroundColor: '#f4f4f4' }}>
            <th style={{ padding: 8 }}>Name</th>
            <th style={{ padding: 8 }}>Host</th>
            <th style={{ padding: 8 }}>Date</th>
            <th style={{ padding: 8 }}>Start ~ End</th>
            <th style={{ padding: 8 }}>Mode</th>
            <th style={{ padding: 8 }}>Purpose</th>
            <th style={{ padding: 8 }}>Age</th>
            <th style={{ padding: 8 }}>Size</th>
            <th style={{ padding: 8 }}>Gender gate</th>
            <th style={{ padding: 8 }}>Orientation</th>
            <th style={{ padding: 8 }}>Event ID</th>
            <th style={{ padding: 8 }}>Bouncer</th>
            <th style={{ padding: 8 }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {liveUpcoming.map(v => (
            <tr key={v._id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: 8 }}>{v.name}</td>
              <td style={{ padding: 8 }}>{hostLabel(v, promoters)}</td>
              <td style={{ padding: 8 }}>{v.eventDate || ''}</td>
              <td style={{ padding: 8 }}>{timeLabel(v)}</td>
              <td style={{ padding: 8 }}>{v.mode || 'matchmaking'}</td>
              <td style={{ padding: 8 }}>{v.purposeTag || '-'}</td>
              <td style={{ padding: 8 }}>{ageLabel(v)}</td>
              <td style={{ padding: 8 }}>{sizeLabel(v)}</td>
              <td style={{ padding: 8 }}>{genderGateLabel(v)}</td>
              <td style={{ padding: 8 }}>{orientationLabel(v)}</td>

              <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                <code>{String(v._id)}</code>{' '}
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(String(v._id))}
                >
                  Copy
                </button>
              </td>

              <td style={{ padding: 8 }}>
                {v.mode !== 'mixer' && (
                  <>
                    <button
                      type="button"
                      onClick={() => generateOrRotateBouncerCode(v._id)}
                      style={{ marginRight: 6 }}
                    >
                      {v.bouncerCodeUpdatedAt ? 'Rotate' : 'Generate'}
                    </button>

                    <button
                      type="button"
                      onClick={() => revealBouncerCode(v._id)}
                    >
                      Reveal
                    </button>
                  </>
                )}

                {issuedCodes[v._id] && (
                  <div style={{ marginTop: 4 }}>
                    Code: <b>{issuedCodes[v._id]}</b>
                  </div>
                )}
              </td>

              <td style={{ padding: 8 }}>
                <button
                  type="button"
                  disabled={savingTemplateId === String(v._id)}
                  onClick={() => handleSaveAsTemplate(v)}
                  style={{ marginRight: 8 }}
                >
                  {savingTemplateId === String(v._id)
                    ? 'Saving...'
                    : 'Save as Template'}
                </button>

                <button
                  onClick={() => handleEditClick(v)}
                  style={{ marginRight: 8 }}
                >
                  Edit
                </button>

                <button onClick={() => handleDelete(v._id, v.mode)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <div>No upcoming events.</div>
    )}
  </>
)}

{eventsView === 'past_live' && (
  <>
    <h3>Past Live Events</h3>

    {livePast.length > 0 ? (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f4f4f4' }}>
            <th style={{ padding: 8 }}>Name</th>
            <th style={{ padding: 8 }}>Host</th>
            <th style={{ padding: 8 }}>Date</th>
            <th style={{ padding: 8 }}>Start ~ End</th>
            <th style={{ padding: 8 }}>Mode</th>
            <th style={{ padding: 8 }}>Purpose</th>
            <th style={{ padding: 8 }}>Age</th>
            <th style={{ padding: 8 }}>Size</th>
            <th style={{ padding: 8 }}>Gender gate</th>
            <th style={{ padding: 8 }}>Orientation</th>
            <th style={{ padding: 8 }}>Event ID</th>
            <th style={{ padding: 8 }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {livePast.map(v => (
            <tr key={v._id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: 8 }}>{v.name}</td>
              <td style={{ padding: 8 }}>{hostLabel(v, promoters)}</td>
              <td style={{ padding: 8 }}>{v.eventDate || ''}</td>
              <td style={{ padding: 8 }}>{timeLabel(v)}</td>
              <td style={{ padding: 8 }}>{v.mode || 'matchmaking'}</td>
              <td style={{ padding: 8 }}>{v.purposeTag || '-'}</td>
              <td style={{ padding: 8 }}>{ageLabel(v)}</td>
              <td style={{ padding: 8 }}>{sizeLabel(v)}</td>
              <td style={{ padding: 8 }}>{genderGateLabel(v)}</td>
              <td style={{ padding: 8 }}>{orientationLabel(v)}</td>

              <td style={{ padding: 8 }}>
                <code>{String(v._id)}</code>
              </td>

              <td style={{ padding: 8 }}>
                <button
                  type="button"
                  disabled={savingTemplateId === String(v._id)}
                  onClick={() => handleSaveAsTemplate(v)}
                  style={{ marginRight: 8 }}
                >
                  {savingTemplateId === String(v._id)
                    ? 'Saving...'
                    : 'Save as Template'}
                </button>

                <button
                  onClick={() => handleEditClick(v)}
                  style={{ marginRight: 8 }}
                >
                  Edit
                </button>

                <button onClick={() => handleDelete(v._id, v.mode)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <div>No past events.</div>
    )}
  </>
)}




        {/* Optional: Past Events */}

      </div>
    );
  }

  // B) RENDER FEEDBACK SECTION
  function renderFeedbackSection() {
    // simple cell
    const td = { borderBottom: '1px solid #eee', padding: '8px', verticalAlign: 'top' };
    const th = { ...td, fontWeight: 800, background: '#fafafa', borderTop: '1px solid #eee' };
    const wrap = { marginTop: 10 };
  
    return (
      <div style={wrap}>
        <h2>Feedback</h2>
  
        {/* A) Event feedback (grouped by eventId) */}
        <h3 style={{ marginTop: 12 }}>Event feedback (by event)</h3>
        {eventFeedback.length === 0 ? (
          <p style={{ color: '#666' }}>No event feedback yet.</p>
        ) : (
       Array.from(eventFeedbackByEvent.entries()).map(([eventId, rows]) => (
  <EventExperienceFeedbackTable
    key={eventId}
    eventId={eventId}
    rows={rows}
    fallbackEventName={getEventNameById(events, eventId)}
  />
))
        )}
  
        {/* B) Problems */}
        <h3 style={{ marginTop: 20 }}>Problems / Issues</h3>
        {problems.length === 0 ? (
          <p style={{ color: '#666' }}>No problem reports.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Title</th>
                <th style={th}>Message</th>
                <th style={th}>User</th>
                <th style={th}>Created</th>
              </tr>
            </thead>
            <tbody>
              {problems.map(fb => (
                <tr key={fb._id}>
                  <td style={td}>{fb.title || '-'}</td>
                  <td style={td}>{fb.message || ''}</td>
                  <td style={td}>
                    {(fb.userName || '').trim() || '(No name)'}
                    {fb.userEmail ? <div style={{ color: '#6b7280', fontSize: 12 }}>{fb.userEmail}</div> : null}
                  </td>
                  <td style={td}>{fb.createdAt ? new Date(fb.createdAt).toLocaleString() : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
  
        {/* C) Suggestions */}
        <h3 style={{ marginTop: 20 }}>Suggestions</h3>
        {suggestions.length === 0 ? (
          <p style={{ color: '#666' }}>No suggestions yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Title</th>
                <th style={th}>Message</th>
                <th style={th}>User</th>
                <th style={th}>Created</th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map(fb => (
                <tr key={fb._id}>
                  <td style={td}>{fb.title || '-'}</td>
                  <td style={td}>{fb.message || ''}</td>
                  <td style={td}>
                    {(fb.userName || '').trim() || '(No name)'}
                    {fb.userEmail ? <div style={{ color: '#6b7280', fontSize: 12 }}>{fb.userEmail}</div> : null}
                  </td>
                  <td style={td}>{fb.createdAt ? new Date(fb.createdAt).toLocaleString() : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }
  

  return (
    <AdminLoginWrapper>
      <div style={{ margin: 20 }}>
        <h2>Admin Panel</h2>

        {/* Admin Tabs */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
  <button
    onClick={() => setAdminTab('events')}
    style={adminTab === 'events' ? { background: '#007bff', color: '#fff' } : {}}
  >
    Manage Events
  </button>
  <button
    onClick={() => setAdminTab('feedback')}
    style={adminTab === 'feedback' ? { background: '#007bff', color: '#fff' } : {}}
  >
    View Feedback
  </button>
  <button
    onClick={() => setAdminTab('promoters')}
    style={adminTab === 'promoters' ? { background: '#007bff', color: '#fff' } : {}}
  >
    Manage Hosts
  </button>
  <button
  onClick={() => setAdminTab('recruiting')}
  style={adminTab === 'recruiting' ? { background: '#007bff', color: '#fff' } : {}}
>
  Host Recruiting
</button>
<button
  onClick={() => setAdminTab('referral_partners')}
  style={
    adminTab === 'referral_partners'
      ? {
          background: '#007bff',
          color: '#fff',
        }
      : {}
  }
>
  Manage Referral Partners
</button>
<button
  onClick={() => setAdminTab('referral_partner_recruiting')}
  style={
    adminTab === 'referral_partner_recruiting'
      ? { background: '#007bff', color: '#fff' }
      : {}
  }
>
  Referral Partner Recruiting
</button>

<button
  onClick={() => setAdminTab('member_applications')}
  style={adminTab === 'member_applications' ? { background: '#007bff', color: '#fff' } : {}}
>
  Membership Applications
</button>

<button
  onClick={() => setAdminTab('users')}
  style={
    adminTab === 'users'
      ? { background: '#007bff', color: '#fff' }
      : {}
  }
>
  Users
</button>

<button
  onClick={() => setAdminTab('cold_outreach')}
  style={
    adminTab === 'cold_outreach'
      ? { background: '#007bff', color: '#fff' }
      : {}
  }
>
  Cold Outreach
</button>
</div>


        {/* Content depending on tab */}
    {adminTab === 'events' && renderEventsSection()}
{adminTab === 'feedback' && renderFeedbackSection()}

{adminTab === 'promoters' && (
  <AdminPromoters onPromoterUpdate={fetchEvents} />
)}

{adminTab === 'recruiting' && (
  <RecruitingPortal />
)}

{adminTab === 'referral_partners' && (
  <ReferralPartnersManager />
)}

{adminTab === 'referral_partner_recruiting' && (
  <ReferralPartnerApplicants />
)}

{adminTab === 'member_applications' && <MemberApplications />}

{adminTab === 'users' && <AdminUsers />}
{adminTab === 'cold_outreach' && <AdminColdOutreach />}

      </div>
    </AdminLoginWrapper>
  );
}
